// @ts-nocheck
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { startAuthentication } from "@simplewebauthn/browser";
import { useLocation, useNavigate } from "react-router-dom";
import { WITHDRAW_HISTORY_REFRESH, withdrawalDeclarationPath } from "../Order/depositDeclarationModel";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import {
  confirmSatoshiAddressBook,
  deleteWalletAddressBook,
  extractAddressBookList,
  extractWithdrawalAddressHistoryList,
  fetchAddressBookPasskeyChallenge,
  fetchAddressBookVerificationOptions,
  fetchPasskeyWithdrawalChallenge,
  fetchWithdrawal24hUsage,
  fetchWalletAddressBook,
  fetchWithdrawCoins,
  fetchWithdrawalAddressHistory,
  fetchWithdrawalSecuritySettings,
  initiateAddressBookWhitelist,
  sendAddressBookVerificationOtp,
  loadWithdrawWalletFunds,
  mapWithdrawError,
  sendWithdrawalVerificationOtp,
  submitWithdrawal,
  cancelUserWithdrawal,
  fetchWithdrawalHistory,
  validateWithdrawAddress,
  verifySignatureAddressBook,
} from "../../../api/services/withdrawService";
import { ProfileContext } from "../../../context/ProfileProvider";
import {
  coinIconSrc,
  coinSymbol,
  extractWithdrawalHistoryList,
  findFundRowBySymbol,
  formatFundAvailable,
  formatWithdrawAmountDisplay,
  getWithdrawNetworksOrStaticFallback,
  mapWithdrawHistoryRow,
  canonicalWithdrawalChainFromUiCode,
  isMetaMaskWhitelistChain,
  parseWalletDepositAddressResponse,
  resolveWithdrawHistoryNetworkForCoin,
  truncateMid,
  addressesEqualForWithdrawalBookMatch,
} from "./withdrawPageHelpers";
import {
  METAMASK_SIGN_FLOW_TIMEOUT_MS,
  promiseWithTimeout,
  signChallengeWithMetaMask,
  hasMetaMaskExtension,
  satoshiConfirmIndicatesProofStillPending,
  addressBookEntryPendingMetaMaskSignature,
  addressBookEntryPendingSatoshiDeposit,
  resolveAddressBookTokenAssetId,
  addressBookMetaMaskChallengeExpiryIso,
  withdrawalAddressValidationKey,
  withdrawStatusTone,
  SAVE_ADDR_COIN_OPTIONS,
  SAVE_ADDR_NETWORK_OPTIONS,
  WITHDRAW_UI_COIN_META,
  WITHDRAW_OTP_RESEND_COOLDOWN_S,
  WITHDRAW_VERIFY_CODE_LEN,
  sanitizeWithdrawAmountRaw,
  TRENDING_COIN_SYMBOLS,
  SAVE_ADDR_WIZARD_STEP,
  ADDRESS_BOOK_TOP_EXCHANGES,
  ADDRESS_BOOK_EXCHANGE_OTHER,
  addressBookEntryWithdrawable,
} from "./withdrawPageShared";

function buildAddressBookBodyBase(core) {
  const o = {
    address: core.address,
    chain: core.chainCanonical,
    coin: core.short_name,
  };
  if (core.label) o.label = core.label;
  if (core.memoTrim) o.memo = core.memoTrim;
  if (core.tokenAssetIdForApi) o.tokenAssetId = core.tokenAssetIdForApi;
  return o;
}

function maskAddrBookEmail(email) {
  if (!email) return "";
  const [local, domain] = String(email).split("@");
  if (!domain) return email;
  const visible = Math.min(2, local.length);
  return `${local.slice(0, visible)}***@${domain}`;
}

function maskAddrBookPhone(p) {
  if (!p) return "";
  const d = String(p).replace(/\D/g, "");
  if (d.length < 4) return "••••";
  return `${d.slice(0, 2)}*****${d.slice(-2)}`;
}

/** Human-readable exchange/VASP label for initiate `exchange_name` */
function resolveAddressBookExchangeName(exchangePick, exchangeManual, topList, otherSentinel) {
  if (exchangePick == null || exchangePick === "") return "";
  if (exchangePick === otherSentinel) return String(exchangeManual || "").trim();
  const row = topList.find((x) => x.value === exchangePick);
  return String(row?.label || exchangePick).trim();
}

/** Default address-book wizard: self ownership, self-hosted wallet, Satoshi verification */
const SAVE_ADDR_DEFAULT_OWNERSHIP = "SELF";
const SAVE_ADDR_DEFAULT_SELF_WALLET_KIND = "SELF_HOSTED";

export function useWithdrawPageController(userDetails = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const profileHydrated = useContext(ProfileContext)?.profileHydrated ?? false;
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(max-width: 767.98px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 767.98px)");
    const onChange = (e) => setIsMobile(!!e.matches);
    setIsMobile(!!mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const stepperWrapRef = useRef(null);
  const [railDims, setRailDims] = useState({ top: 0, height: 0, fill: 0 });
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  const [emailVerifyCode, setEmailVerifyCode] = useState("");
  const [withdrawOtpSending, setWithdrawOtpSending] = useState({ email: false, mobile: false });
  const [withdrawOtpResendSec, setWithdrawOtpResendSec] = useState({ email: 0, mobile: 0 });
  const [withdrawWithdrawalFlowBusy, setWithdrawWithdrawalFlowBusy] = useState(false);
  const [withdrawConfirmBusy, setWithdrawConfirmBusy] = useState(false);
  const [withdrawSummaryContinueBusy, setWithdrawSummaryContinueBusy] = useState(false);
  const withdrawalRequestLockRef = useRef(false);
  const withdrawHistoryPromiseRef = useRef(null);
  const [selectedCoin, setSelectedCoin] = useState("USDT");
  const [withdrawCoins, setWithdrawCoins] = useState([]);
  const [loadingWithdrawCoins, setLoadingWithdrawCoins] = useState(true);
  const [coinSearchQuery, setCoinSearchQuery] = useState("");
  const [recentWithdrawRows, setRecentWithdrawRows] = useState([]);
  const [loadingWithdrawHistory, setLoadingWithdrawHistory] = useState(true);
  const [withdrawCancelBusy, setWithdrawCancelBusy] = useState(false);
  // Balances for Main Wallet (user-wallet, spot).
  const [fundMain, setFundMain] = useState(null);
  const [selectedWithdrawHistoryRow, setSelectedWithdrawHistoryRow] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState("Select Network");
  const [isWithdrawDetailsOpen] = useState(true);
  // Withdraw to: Address (on-chain) vs AGCX User (internal)
  const [withdrawToTab, setWithdrawToTab] = useState("address");
  // AGCX User sub-tabs
  const [agceRecipientTab, setAgceRecipientTab] = useState("email");
  // AGCX User -> recipient inputs + validation
  const [agceRecipientEmail, setAgceRecipientEmail] = useState("");
  const [agceRecipientPhone, setAgceRecipientPhone] = useState("");
  const [agceRecipientId, setAgceRecipientId] = useState("");
  const [agceRecipientUsername] = useState("User-2404d");
  const [agceTouched, setAgceTouched] = useState({ email: false, phone: false });
  // Address book panel (user icon button)
  const [showAddressBookPanel, setShowAddressBookPanel] = useState(false);
  // Inner tabs: Recent (withdrawal address history) vs My Address (saved / whitelisted)
  const [addressBookSubTab, setAddressBookSubTab] = useState("my_address");
  // Step 2: Address tab input
  const [withdrawAddress, setWithdrawAddress] = useState("");
  const [withdrawAddressTouched, setWithdrawAddressTouched] = useState(false);
  const [withdrawAddressValidError, setWithdrawAddressValidError] = useState("");
  const [withdrawAddressValidatedKey, setWithdrawAddressValidatedKey] = useState("");
  const [isWithdrawAddressValidating, setIsWithdrawAddressValidating] = useState(false);
  const withdrawAddressValidationReqIdRef = useRef(0);
  const withdrawAddrValidateDebounceTimerRef = useRef(null);
  const withdrawConfirmProceedTimerRef = useRef(null);
  const satoshiWhitelistConfirmInFlightRef = useRef(false);
  // Step 3: Available Withdraw source selector
  const [isAvailSourceOpen, setIsAvailSourceOpen] = useState(false);
  const [isAvailSourcePinned, setIsAvailSourcePinned] = useState(false);
  const [isAvailSourceContextLock, setIsAvailSourceContextLock] = useState(false);
  // Step 3: inline “About Network Fee” panel (not a modal)
  const [isNetworkFeeInfoOpen, setIsNetworkFeeInfoOpen] = useState(false);
  const [withdrawAmountInput, setWithdrawAmountInput] = useState("");
  const [withdrawAmountTouched, setWithdrawAmountTouched] = useState(false);
  const [isLimitInfoOpen, setIsLimitInfoOpen] = useState(false);
  const [isWithdrawMetaRefreshing, setIsWithdrawMetaRefreshing] = useState(false);
  const availSourceRef = useRef(null);

  useEffect(() => {
    if (!isAvailSourceOpen) return undefined;

    const onPointerDown = (e) => {
      // Keep right-click context menu behavior intact for Inspect.
      if (e.button === 2) return;
      const el = e.target;
      if (availSourceRef.current?.contains(el)) return;
      setIsAvailSourcePinned(false);
      setIsAvailSourceContextLock(false);
      setIsAvailSourceOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsAvailSourcePinned(false);
        setIsAvailSourceContextLock(false);
        setIsAvailSourceOpen(false);
      }
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [isAvailSourceOpen]);

  // AGCX User -> Phone: country code dropdown
  const [phoneCountry, setPhoneCountry] = useState({ flag: "🇮🇳", code: "+91" });
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const countryRef = useRef(null);

  // Step 3: withdrawal confirm popup (network warning)
  const [isWithdrawConfirmOpen, setIsWithdrawConfirmOpen] = useState(false);
  const withdrawConfirmRef = useRef(null);
  const [isAddressVerificationReminderOpen, setIsAddressVerificationReminderOpen] = useState(false);
  // Step 3: withdrawal summary popup (details + continue)
  const [isWithdrawSummaryOpen, setIsWithdrawSummaryOpen] = useState(false);
  const withdrawSummaryRef = useRef(null);
  // Step 3: passkey verification popup
  const [isPasskeyOpen, setIsPasskeyOpen] = useState(false);
  const passkeyRef = useRef(null);
  // Step 3: authenticator app verification popup
  const [isAuthAppOpen, setIsAuthAppOpen] = useState(false);
  const authAppRef = useRef(null);
  const [authAppCode, setAuthAppCode] = useState("");
  // Step 3: choose verification method popup
  const [isChooseAuthOpen, setIsChooseAuthOpen] = useState(false);
  const chooseAuthRef = useRef(null);
  // Step 3: email verification popup
  const [isEmailVerifyOpen, setIsEmailVerifyOpen] = useState(false);
  const emailVerifyRef = useRef(null);
  // Step 3: mobile verification popup
  const [isMobileVerifyOpen, setIsMobileVerifyOpen] = useState(false);
  const mobileVerifyRef = useRef(null);
  // Security verification unavailable flow (screenshots)
  const [isSecUnavailableOpen, setIsSecUnavailableOpen] = useState(false);
  const [secUnavailable, setSecUnavailable] = useState({
    email: true,
    phone: false,
    authApp: false,
    passkeys: false,
  });
  const [isSecResetConfirmOpen, setIsSecResetConfirmOpen] = useState(false);
  const [secResetAck, setSecResetAck] = useState(true);
  const [isSecReqOpen, setIsSecReqOpen] = useState(false);
  const [secReqDone, setSecReqDone] = useState({
    authApp: false,
    email: false,
    phone: false,
    passkeys: false,
  });

  const [isRequestSubmittedOpen, setIsRequestSubmittedOpen] = useState(false);
  const [isSecurityResetSuccessOpen, setIsSecurityResetSuccessOpen] = useState(false);
  const closeResetResultOverlays = () => {
    setIsRequestSubmittedOpen(false);
    setIsSecurityResetSuccessOpen(false);
  };
  const [verificationContext, setVerificationContext] = useState("withdraw"); // 'withdraw' | 'reset' | 'address_book'

  // Withdrawal security settings fetched from backend
  const [withdrawSecuritySettings, setWithdrawSecuritySettings] = useState(null);
  // Selected verification method for current withdrawal
  const [withdrawVerifyMethod, setWithdrawVerifyMethod] = useState("email");
  // Fund password input for withdrawal verification
  const [withdrawFundPassword, setWithdrawFundPassword] = useState("");
  const [withdrawFundPasswordError, setWithdrawFundPasswordError] = useState("");
  const [withdrawFundPasswordVisible, setWithdrawFundPasswordVisible] = useState(false);
  // Show "no methods enabled" blocking modal
  const [isNoMethodsModalOpen, setIsNoMethodsModalOpen] = useState(false);
  // Non-passkey methods: single modal with all OTP / fund password inputs
  const [isWithdrawVerifyCombinedOpen, setIsWithdrawVerifyCombinedOpen] = useState(false);
  const [withdrawVerifyFieldErrors, setWithdrawVerifyFieldErrors] = useState({
    email: "",
    mobile: "",
    google_authenticator: "",
    fund_password: "",
  });
  // Mobile OTP code for withdrawal
  const [mobileVerifyCode, setMobileVerifyCode] = useState("");
  // After passkey modal: verify with email/SMS/authenticator/fund password instead
  const [withdrawOtpFallbackAfterPasskey, setWithdrawOtpFallbackAfterPasskey] = useState(false);

  const closeAllOverlays = () => {
    if (withdrawConfirmProceedTimerRef.current) {
      window.clearTimeout(withdrawConfirmProceedTimerRef.current);
      withdrawConfirmProceedTimerRef.current = null;
    }
    setIsNetworkFeeInfoOpen(false);
    setIsWithdrawConfirmOpen(false);
    setIsAddressVerificationReminderOpen(false);
    setIsWithdrawSummaryOpen(false);
    setIsPasskeyOpen(false);
    setIsAuthAppOpen(false);
    setIsChooseAuthOpen(false);
    setIsEmailVerifyOpen(false);
    setIsMobileVerifyOpen(false);
    setIsAwaitingApprovalOpen(false);
    setIsRecipientAmountOpen(false);
    setIsSaveAddressOpen(false);
    setIsSetPayPinOpen(false);

    setIsSecUnavailableOpen(false);
    setIsSecResetConfirmOpen(false);
    setIsSecReqOpen(false);
    setIsNoMethodsModalOpen(false);
    setIsWithdrawVerifyCombinedOpen(false);
    setWithdrawOtpFallbackAfterPasskey(false);
    setWithdrawFundPasswordVisible(false);

    closeResetResultOverlays();
  };
  // Step 3: awaiting approval popup
  const awaitingFromUrl = new URLSearchParams(location.search).get("awaiting") === "1";
  const [isAwaitingApprovalOpen, setIsAwaitingApprovalOpen] = useState(awaitingFromUrl);

  useEffect(() => {
    let fromWindow = false;
    try {
      fromWindow = new URLSearchParams(window.location.search).get("awaiting") === "1";
    } catch {
      fromWindow = false;
    }
    if (awaitingFromUrl || fromWindow) setIsAwaitingApprovalOpen(true);
  }, [awaitingFromUrl]);
  const awaitingApprovalRef = useRef(null);
  // Step 3: recipient amount confirmation popup
  const [isRecipientAmountOpen, setIsRecipientAmountOpen] = useState(false);
  const recipientAmountRef = useRef(null);
  // Step 3: Set Pay PIN popup (after internal transfer)
  const [isSetPayPinOpen, setIsSetPayPinOpen] = useState(false);
  const [payPinDigits, setPayPinDigits] = useState(["", "", "", "", "", ""]);
  const [payPinShow, setPayPinShow] = useState(false);
  const payPinRefs = useRef([]);
  // Save address (from recipient row user icon)
  const [isSaveAddressOpen, setIsSaveAddressOpen] = useState(false);
  const [saveAddrLabel, setSaveAddrLabel] = useState("");
  const [saveAddrCoin, setSaveAddrCoin] = useState("");
  const [saveAddrUniversal, setSaveAddrUniversal] = useState(false);
  const [saveAddrValue, setSaveAddrValue] = useState("");
  const [saveAddrNetwork, setSaveAddrNetwork] = useState("");
  const [saveAddrMemo, setSaveAddrMemo] = useState("");
  const [saveAddrWhitelistMethod, setSaveAddrWhitelistMethod] = useState("SATOSHI");
  const [saveAddrWizardStep, setSaveAddrWizardStep] = useState(SAVE_ADDR_WIZARD_STEP.BASICS);
  const [saveAddrOwnership, setSaveAddrOwnership] = useState(SAVE_ADDR_DEFAULT_OWNERSHIP);
  const [saveAddrWalletKindSelf, setSaveAddrWalletKindSelf] = useState(SAVE_ADDR_DEFAULT_SELF_WALLET_KIND);
  const [saveAddrWalletKindOther, setSaveAddrWalletKindOther] = useState("");
  const [saveAddrExchangePick, setSaveAddrExchangePick] = useState("");
  const [saveAddrExchangeManual, setSaveAddrExchangeManual] = useState("");
  const [saveAddrDeclarationAccepted, setSaveAddrDeclarationAccepted] = useState(false);
  const [saveAddrBenFullName, setSaveAddrBenFullName] = useState("");
  const [saveAddrBenPan, setSaveAddrBenPan] = useState("");
  const [saveAddrBenCountry, setSaveAddrBenCountry] = useState("");
  const [saveAddrBenPin, setSaveAddrBenPin] = useState("");
  const [saveAddrBenAddress, setSaveAddrBenAddress] = useState("");
  // null = step 1 form; otherwise step 2 (Satoshi or MetaMask).
  const [addressWhitelistFlow, setAddressWhitelistFlow] = useState(null);
  const [satoshiDepositAddress, setSatoshiDepositAddress] = useState("");
  const [satoshiDepositMemo, setSatoshiDepositMemo] = useState("");
  const [satoshiDepositLoading, setSatoshiDepositLoading] = useState(false);
  const [satoshiDepositError, setSatoshiDepositError] = useState(null);
  const [saveAddrSatoshiPolling, setSaveAddrSatoshiPolling] = useState(false);
  // User tapped "I've sent" once; proof not seen yet — show async message and allow another single confirm.
  const [satoshiWhitelistAwaitingProof, setSatoshiWhitelistAwaitingProof] = useState(false);
  const [whitelistMetamaskBusy, setWhitelistMetamaskBusy] = useState(false);
  const [metamaskInstallModalOpen, setMetamaskInstallModalOpen] = useState(false);
  // Wallet injection is often async (ethereum#initialized); MetaMask presence for the verify step.
  const [eip1193BrowserProviderDetected, setEip1193BrowserProviderDetected] = useState(() =>
    typeof window !== "undefined" && hasMetaMaskExtension()
  );
  const [saveAddrSubmitAttempted, setSaveAddrSubmitAttempted] = useState(false);
  const [saveAddrSaving, setSaveAddrSaving] = useState(false);
  const [addrBookVerificationOptionsState, setAddrBookVerificationOptionsState] = useState(null);
  const [addrBookVerificationOptionsLoading, setAddrBookVerificationOptionsLoading] = useState(false);
  /** idle: choose method in save-address wizard | otp | passkey */
  const [addrBookVerifyStage, setAddrBookVerifyStage] = useState("idle");
  const [addrBookOtpKind, setAddrBookOtpKind] = useState(null);
  const [addrBookOtpBoxes, setAddrBookOtpBoxes] = useState(["", "", "", "", "", ""]);
  const [addrBookOtpBusy, setAddrBookOtpBusy] = useState(false);
  const [addrBookOtpSending, setAddrBookOtpSending] = useState({ email: false, mobile: false });
  const [addrBookOtpResendSec, setAddrBookOtpResendSec] = useState({ email: 0, mobile: 0 });
  const [addrBookWizardSelectedMethod, setAddrBookWizardSelectedMethod] = useState(null);
  const addressBookVerificationEntryOpenedRef = useRef(false);
  const [saveAddrAddressValidError, setSaveAddrAddressValidError] = useState("");
  const [saveAddrAddressValidatedKey, setSaveAddrAddressValidatedKey] = useState("");
  const [saveAddrAddressValidating, setSaveAddrAddressValidating] = useState(false);
  const [addressBookEntries, setAddressBookEntries] = useState([]);
  const [addressBookLoading, setAddressBookLoading] = useState(false);
  const [withdrawalAddressHistory, setWithdrawalAddressHistory] = useState([]);
  const [withdrawalAddressHistoryLoading, setWithdrawalAddressHistoryLoading] = useState(false);
  // Address book: confirm before DELETE
  const [addressBookDeleteConfirm, setAddressBookDeleteConfirm] = useState(null);
  const [addressBookDeleteBusy, setAddressBookDeleteBusy] = useState(false);
  // Save Address modal — custom Coin / Network dropdowns
  const [saveCoinMenuOpen, setSaveCoinMenuOpen] = useState(false);
  const [saveNetworkMenuOpen, setSaveNetworkMenuOpen] = useState(false);
  const [saveExchangeMenuOpen, setSaveExchangeMenuOpen] = useState(false);
  const saveCoinSelectRef = useRef(null);
  const saveNetworkSelectRef = useRef(null);
  const saveExchangeSelectRef = useRef(null);
  const addressBookPendingInitiateBodyRef = useRef(null);
  const addressBookPendingInitiateCoreRef = useRef(null);
  const addressBookVerifyReturnStepRef = useRef(SAVE_ADDR_WIZARD_STEP.BASICS);
  const saveAddrValidateReqIdRef = useRef(0);
  const saveAddrValidateDebounceTimerRef = useRef(null);

  const closeAddressBookVerificationUi = useCallback(() => {
    setVerificationContext((prev) => (prev === "address_book" ? "withdraw" : prev));
    setIsPasskeyOpen(false);
    setIsWithdrawVerifyCombinedOpen(false);
    setIsEmailVerifyOpen(false);
    setIsMobileVerifyOpen(false);
    setIsAuthAppOpen(false);
    setIsChooseAuthOpen(false);
    setAddrBookOtpSending({ email: false, mobile: false });
    setAddrBookOtpResendSec({ email: 0, mobile: 0 });
    setAddrBookVerifyStage("idle");
    setAddrBookOtpKind(null);
    setAddrBookOtpBoxes(["", "", "", "", "", ""]);
    setAddrBookOtpBusy(false);
    setAddrBookWizardSelectedMethod(null);
  }, []);
  // Recent withdrawals → Withdrawal Details (custom overlay; replaces Bootstrap #recent_table)
  const [isWithdrawHistoryDetailOpen, setIsWithdrawHistoryDetailOpen] = useState(false);
  // Recent list: filter UI (wire to API when rows include error states)
  const [hideWithdrawErrorNotices, setHideWithdrawErrorNotices] = useState(false);
  // Address vs AGCX user tab
  const [recentWithdrawTab, setRecentWithdrawTab] = useState("address");
  const recentWithdrawRowsVisible = useMemo(() => {
    if (!hideWithdrawErrorNotices) return recentWithdrawRows;
    return recentWithdrawRows.filter((row) => withdrawStatusTone(row.statusLabel) !== "danger");
  }, [recentWithdrawRows, hideWithdrawErrorNotices]);
  const [withdraw24hUsage, setWithdraw24hUsage] = useState(null);

  useEffect(() => {
    if (!isSaveAddressOpen) {
      setSaveCoinMenuOpen(false);
      setSaveNetworkMenuOpen(false);
      setSaveExchangeMenuOpen(false);
    }
  }, [isSaveAddressOpen]);

  useEffect(() => {
    if (!isSaveAddressOpen || saveAddrWizardStep !== SAVE_ADDR_WIZARD_STEP.VERIFY_ACCOUNT) return undefined;
    let cancelled = false;
    (async () => {
      setAddrBookVerificationOptionsLoading(true);
      try {
        const res = await fetchAddressBookVerificationOptions();
        if (cancelled) return;
        if (res?.success === false) {
          alertErrorMessage(res?.message || "Could not load verification options.");
          setAddrBookVerificationOptionsState(null);
          return;
        }
        const d = res?.data ?? {};
        const methods = Array.isArray(d.available_methods) ? d.available_methods : [];
        let rec =
          d.recommended != null && String(d.recommended).trim() ? String(d.recommended).trim() : "";
        if (!methods.includes(rec)) rec = methods[0] || "";
        setAddrBookVerificationOptionsState({
          available_methods: methods,
          recommended: rec,
        });
      } catch {
        if (!cancelled) {
          alertErrorMessage("Could not load verification options.");
          setAddrBookVerificationOptionsState(null);
        }
      } finally {
        if (!cancelled) setAddrBookVerificationOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      addressBookVerificationEntryOpenedRef.current = false;
    };
  }, [isSaveAddressOpen, saveAddrWizardStep]);

  useEffect(() => {
    if (!isSaveAddressOpen) return undefined;

    const refresh = () => setEip1193BrowserProviderDetected(hasMetaMaskExtension());
    refresh();

    const mmStep = addressWhitelistFlow?.type === "metamask";
    if (!mmStep) return undefined;

    window.addEventListener("ethereum#initialized", refresh);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    const pollMs = 350;
    const poll = window.setInterval(refresh, pollMs);
    const stopPoll = window.setTimeout(() => window.clearInterval(poll), 60_000);

    return () => {
      window.removeEventListener("ethereum#initialized", refresh);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(poll);
      window.clearTimeout(stopPoll);
    };
  }, [isSaveAddressOpen, addressWhitelistFlow?.type, addressWhitelistFlow?.id]);

  useEffect(() => {
    if (addressWhitelistFlow?.type !== "satoshi") {
      setSatoshiWhitelistAwaitingProof(false);
      return;
    }
    setSatoshiWhitelistAwaitingProof(false);
  }, [addressWhitelistFlow?.type, addressWhitelistFlow?.id]);

  const reloadWithdrawCatalog = useCallback(async () => {
    setLoadingWithdrawCoins(true);
    try {
      const r = await fetchWithdrawCoins();
      setWithdrawCoins(r.coins || []);
    } catch {
      setWithdrawCoins([]);
    } finally {
      setLoadingWithdrawCoins(false);
    }
  }, []);

  useEffect(() => {
    reloadWithdrawCatalog();
  }, [reloadWithdrawCatalog]);

  const reloadWithdrawFunds = useCallback(async () => {
    try {
      const { funds } = await loadWithdrawWalletFunds();
      setFundMain(funds);
    } catch {
      /* keep last known balances */
    }
  }, []);

  useEffect(() => {
    void reloadWithdrawFunds();
  }, [reloadWithdrawFunds]);

  const reloadWithdrawal24hUsage = useCallback(async () => {
    const coin = String(selectedCoin || "").trim();
    if (!coin) {
      setWithdraw24hUsage(null);
      return;
    }
    try {
      const res = await fetchWithdrawal24hUsage(coin);
      if (res?.success && res?.data) {
        setWithdraw24hUsage(res.data);
      } else {
        setWithdraw24hUsage(null);
      }
    } catch {
      setWithdraw24hUsage(null);
    }
  }, [selectedCoin]);

  useEffect(() => {
    void reloadWithdrawal24hUsage();
  }, [reloadWithdrawal24hUsage]);

  const refetchWithdrawHistory = useCallback(async () => {
    if (withdrawHistoryPromiseRef.current) {
      return withdrawHistoryPromiseRef.current;
    }
    const p = (async () => {
      setLoadingWithdrawHistory(true);
      try {
        const res = await fetchWithdrawalHistory(0, 20);
        const raw = extractWithdrawalHistoryList(res);
        setRecentWithdrawRows(raw.map((row, i) => mapWithdrawHistoryRow(row, i)));
      } catch {
        setRecentWithdrawRows([]);
      } finally {
        setLoadingWithdrawHistory(false);
        withdrawHistoryPromiseRef.current = null;
      }
    })();
    withdrawHistoryPromiseRef.current = p;
    return p;
  }, []);

  useEffect(() => {
    const onRefresh = () => {
      void refetchWithdrawHistory();
      void reloadWithdrawFunds();
    };
    window.addEventListener(WITHDRAW_HISTORY_REFRESH, onRefresh);
    return () => window.removeEventListener(WITHDRAW_HISTORY_REFRESH, onRefresh);
  }, [refetchWithdrawHistory, reloadWithdrawFunds]);

  useEffect(() => {
    refetchWithdrawHistory();
  }, [refetchWithdrawHistory]);

  const refetchAddressBook = useCallback(async () => {
    setAddressBookLoading(true);
    try {
      const res = await fetchWalletAddressBook();
      setAddressBookEntries(extractAddressBookList(res));
    } catch {
      setAddressBookEntries([]);
    } finally {
      setAddressBookLoading(false);
    }
  }, []);

  const refetchWithdrawalAddressHistory = useCallback(async () => {
    setWithdrawalAddressHistoryLoading(true);
    try {
      const res = await fetchWithdrawalAddressHistory();
      setWithdrawalAddressHistory(extractWithdrawalAddressHistoryList(res));
    } catch {
      setWithdrawalAddressHistory([]);
    } finally {
      setWithdrawalAddressHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    refetchAddressBook();
  }, [refetchAddressBook]);

  useEffect(() => {
    refetchWithdrawalAddressHistory();
  }, [refetchWithdrawalAddressHistory]);

  // Fetch withdrawal security settings once on mount
  // Auto-open security warning if no method is configured
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithdrawalSecuritySettings();
        if (cancelled || !res?.success) return;
        setWithdrawSecuritySettings(res.data?.settings || null);
        // Show warning popup if user has no active withdrawal verification method
        const activeMethods = res.data?.active_methods || {};
        const hasActive = Object.values(activeMethods).some(Boolean);
        if (!hasActive) {
          setIsNoMethodsModalOpen(true);
        }
      } catch {
        // ignore — fall back to default (email)
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefer USDT when catalog loads; fall back to first coin if USDT is missing.
  useEffect(() => {
    if (loadingWithdrawCoins || withdrawCoins.length === 0) return;
    const sym = String(selectedCoin || "").toUpperCase();
    const match = withdrawCoins.some((c) => coinSymbol(c).toUpperCase() === sym);
    if (match) return;
    const usdtRow = withdrawCoins.find((c) => coinSymbol(c).toUpperCase() === "USDT");
    setSelectedCoin(usdtRow ? "USDT" : coinSymbol(withdrawCoins[0]).toUpperCase());
  }, [loadingWithdrawCoins, withdrawCoins, selectedCoin]);

  useEffect(() => {
    if (!isSaveAddressOpen) return;
    if (selectedCoin) setSaveAddrCoin(String(selectedCoin).toUpperCase());
    if (selectedNetwork && selectedNetwork !== "Select Network") setSaveAddrNetwork(selectedNetwork);
  }, [isSaveAddressOpen, selectedCoin, selectedNetwork]);

  const payPinValue = payPinDigits.join("");
  const isPayPinComplete = payPinDigits.every((d) => /^\d$/.test(d));

  const resetPayPin = () => {
    setPayPinDigits(["", "", "", "", "", ""]);
    setPayPinShow(false);
  };

  const handlePayPinDigit = (idx, raw) => {
    const v = String(raw || "").replace(/\D/g, "").slice(-1);
    setPayPinDigits((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
    if (v && payPinRefs.current[idx + 1]) payPinRefs.current[idx + 1].focus();
  };

  const handlePayPinKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !payPinDigits[idx] && payPinRefs.current[idx - 1]) {
      payPinRefs.current[idx - 1].focus();
    }
    if (e.key === "ArrowLeft" && payPinRefs.current[idx - 1]) payPinRefs.current[idx - 1].focus();
    if (e.key === "ArrowRight" && payPinRefs.current[idx + 1]) payPinRefs.current[idx + 1].focus();
  };

  useEffect(() => {
    if (!isSetPayPinOpen) return;
    const t = setTimeout(() => payPinRefs.current?.[0]?.focus?.(), 0);
    return () => clearTimeout(t);
  }, [isSetPayPinOpen]);

  useEffect(() => {
    if (!saveCoinMenuOpen && !saveNetworkMenuOpen && !saveExchangeMenuOpen) return;
    const onPointerDown = (e) => {
      const el = e.target;
      if (saveCoinSelectRef.current?.contains(el)) return;
      if (saveNetworkSelectRef.current?.contains(el)) return;
      if (saveExchangeSelectRef.current?.contains(el)) return;
      setSaveCoinMenuOpen(false);
      setSaveNetworkMenuOpen(false);
      setSaveExchangeMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [saveCoinMenuOpen, saveNetworkMenuOpen, saveExchangeMenuOpen]);

  // Note: per requirement, popups do NOT close on outside click or Esc.

  // No hover timers for popups.


  const isStep2Done = useMemo(() => {
    if (withdrawToTab === "address") {
      const addr = withdrawAddress.trim();
      const net = selectedNetwork !== "Select Network" ? selectedNetwork : "";
      if (!addr || !net) return false;
      const coin = withdrawCoins.find((c) => coinSymbol(c).toUpperCase() === String(selectedCoin).toUpperCase());
      const tid =
        coin?.token_asset_ids?.[net] != null ? String(coin.token_asset_ids[net]).trim() : "";
      return withdrawAddressValidatedKey === withdrawalAddressValidationKey(net, addr, tid);
    }
    if (agceRecipientTab === "email") {
      const email = agceRecipientEmail.trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }
    if (agceRecipientTab === "phone") {
      const digits = agceRecipientPhone.trim().replace(/\D/g, "");
      return digits.length >= 7 && digits.length <= 15;
    }
    return agceRecipientId.trim().length > 0;
  }, [
    withdrawToTab,
    selectedNetwork,
    withdrawAddress,
    withdrawAddressValidatedKey,
    withdrawCoins,
    selectedCoin,
    agceRecipientTab,
    agceRecipientEmail,
    agceRecipientPhone,
    agceRecipientId,
  ]);

  const selectedCoinObj = useMemo(
    () => withdrawCoins.find((c) => coinSymbol(c).toUpperCase() === String(selectedCoin).toUpperCase()) || null,
    [withdrawCoins, selectedCoin]
  );

  const isStep1Done = useMemo(() => Boolean(selectedCoinObj), [selectedCoinObj]);

  // Clamp the rail to visible step badges (fix: line should end at step 3 badge).
  useLayoutEffect(() => {
    const el = stepperWrapRef.current;
    if (!el) return;

    const compute = () => {
      try {
        const badges = Array.from(el.querySelectorAll(".deposit_step_badge"));
        if (badges.length < 2) {
          setRailDims({ top: 0, height: 0, fill: 0 });
          return;
        }
        const wrapRect = el.getBoundingClientRect();
        const firstRect = badges[0].getBoundingClientRect();
        const lastRect = badges[badges.length - 1].getBoundingClientRect();
        const firstCenter = firstRect.top - wrapRect.top + firstRect.height / 2;
        const lastCenter = lastRect.top - wrapRect.top + lastRect.height / 2;
        const top = Math.max(0, Math.round(firstCenter));
        const height = Math.max(0, Math.round(lastCenter - firstCenter));
        // Fill: step1 done -> reach step2 center, step2 done -> reach last step center.
        const targetIdx = isStep2Done ? badges.length - 1 : isStep1Done ? 1 : 0;
        const targetRect = badges[Math.min(Math.max(targetIdx, 0), badges.length - 1)].getBoundingClientRect();
        const targetCenter = targetRect.top - wrapRect.top + targetRect.height / 2;
        const fill = Math.max(0, Math.round(targetCenter - firstCenter));
        setRailDims({ top, height, fill });
      } catch {
        // ignore
      }
    };

    const raf = window.requestAnimationFrame(compute);
    let ro = null;
    try {
      ro = new ResizeObserver(() => compute());
      ro.observe(el);
    } catch {
      /* ignore */
    }
    window.addEventListener("resize", compute);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", compute);
      try {
        ro?.disconnect?.();
      } catch {
        /* ignore */
      }
    };
  }, [isStep1Done, isStep2Done, withdrawToTab, loadingWithdrawCoins, withdrawSubmitting]);

  const activeWithdrawNetworks = useMemo(
    () => (selectedCoinObj ? getWithdrawNetworksOrStaticFallback(selectedCoinObj) : []),
    [selectedCoinObj]
  );

  const minWithdrawForSelection = useMemo(() => {
    if (!selectedCoinObj || !selectedNetwork || selectedNetwork === "Select Network") return 0;
    const selectedNet = activeWithdrawNetworks.find((n) => n.code === selectedNetwork);
    const min = Number(selectedNet?.min);
    return Number.isFinite(min) && min > 0 ? min : 0;
  }, [selectedCoinObj, selectedNetwork, activeWithdrawNetworks]);

  const effectiveAvailable = useMemo(() => {
    return parseFloat(formatFundAvailable(findFundRowBySymbol(fundMain, selectedCoin))) || 0;
  }, [fundMain, selectedCoin]);

  const filteredWithdrawCoins = useMemo(() => {
    const q = coinSearchQuery.trim().toLowerCase();
    if (!q) return withdrawCoins;
    return withdrawCoins.filter(
      (c) =>
        coinSymbol(c).toLowerCase().includes(q) || String(c.name || "").toLowerCase().includes(q)
    );
  }, [withdrawCoins, coinSearchQuery]);

  const trendingWithdrawCoins = useMemo(() => {
    if (!Array.isArray(withdrawCoins) || withdrawCoins.length === 0) return [];
    const bySym = new Map();
    for (const c of withdrawCoins) {
      const sym = coinSymbol(c).toUpperCase();
      if (!sym || sym === "—") continue;
      if (!bySym.has(sym)) bySym.set(sym, c);
    }
    const list = [];
    for (const sym of TRENDING_COIN_SYMBOLS) {
      const c = bySym.get(sym);
      if (c) list.push(c);
    }
    return list;
  }, [withdrawCoins]);

  const coinModalDisplay = useMemo(() => {
    const q = coinSearchQuery.trim();
    if (q) return filteredWithdrawCoins;
    if (!trendingWithdrawCoins.length) return filteredWithdrawCoins;
    const trendSet = new Set(trendingWithdrawCoins.map((c) => coinSymbol(c).toUpperCase()).filter(Boolean));
    return [
      ...trendingWithdrawCoins,
      ...filteredWithdrawCoins.filter((c) => !trendSet.has(coinSymbol(c).toUpperCase())),
    ];
  }, [coinSearchQuery, filteredWithdrawCoins, trendingWithdrawCoins]);

  const coinQuickPickDisplay = useMemo(() => {
    if (!filteredWithdrawCoins.length) return [];
    const q = coinSearchQuery.trim();
    if (q) return filteredWithdrawCoins.slice(0, 5);
    const ordered = coinModalDisplay;
    return ordered.slice(0, 5);
  }, [coinSearchQuery, filteredWithdrawCoins, coinModalDisplay]);

  const saveAddrCoinOptionsList = useMemo(
    () =>
      withdrawCoins.length
        ? withdrawCoins.map((c) => {
          const v = coinSymbol(c).toUpperCase();
          return { value: v, label: v };
        })
        : SAVE_ADDR_COIN_OPTIONS,
    [withdrawCoins]
  );

  const saveAddrNetworkOptionsEffective = useMemo(() => {
    const coin = withdrawCoins.find(
      (c) => coinSymbol(c).toUpperCase() === String(saveAddrCoin || "").toUpperCase()
    );
    if (coin && saveAddrCoin) {
      const nets = getWithdrawNetworksOrStaticFallback(coin);
      if (nets.length) {
        return nets.map((n) => ({
          value: n.code,
          label: n.label,
          chainCode: n.code,
          tokenAssetId: coin.token_asset_ids?.[n.code] || "",
        }));
      }
    }
    return SAVE_ADDR_NETWORK_OPTIONS.map((o) => ({
      value: String(o.value).toUpperCase(),
      label: o.label,
      chainCode: String(o.value).toUpperCase(),
      tokenAssetId: "",
    }));
  }, [saveAddrCoin, withdrawCoins]);

  useEffect(() => {
    const codes = new Set(saveAddrNetworkOptionsEffective.map((o) => o.value));
    if (saveAddrNetwork && !codes.has(saveAddrNetwork)) setSaveAddrNetwork("");
  }, [saveAddrNetworkOptionsEffective, saveAddrNetwork]);

  const saveAddrChainCanonical = useMemo(() => {
    const netOpt = saveAddrNetworkOptionsEffective.find((o) => o.value === saveAddrNetwork);
    const raw = netOpt?.chainCode ?? saveAddrNetwork;
    const s = raw != null && String(raw).trim() ? String(raw).trim() : "";
    if (!s || s.toUpperCase() === "SELECT NETWORK") return "";
    return canonicalWithdrawalChainFromUiCode(s) || s.toUpperCase();
  }, [saveAddrNetwork, saveAddrNetworkOptionsEffective]);

  const saveAddrTokenAssetId = useMemo(() => {
    const netOpt = saveAddrNetworkOptionsEffective.find((o) => o.value === saveAddrNetwork);
    return netOpt?.tokenAssetId != null && String(netOpt.tokenAssetId).trim()
      ? String(netOpt.tokenAssetId).trim()
      : "";
  }, [saveAddrNetworkOptionsEffective, saveAddrNetwork]);

  useEffect(() => {
    if (!isMetaMaskWhitelistChain(saveAddrChainCanonical)) {
      setSaveAddrWhitelistMethod("SATOSHI");
    }
  }, [saveAddrChainCanonical]);
  const selectedCoinMeta = useMemo(() => {
    if (selectedCoinObj) {
      return {
        name: selectedCoinObj.name || WITHDRAW_UI_COIN_META[selectedCoin]?.name || selectedCoin,
        icon: coinIconSrc(selectedCoinObj),
      };
    }
    if (selectedCoin && WITHDRAW_UI_COIN_META[selectedCoin]) {
      return WITHDRAW_UI_COIN_META[selectedCoin];
    }
    return { name: "", icon: "" };
  }, [selectedCoin, selectedCoinObj]);

  useEffect(() => {
    if (!selectedCoinObj || selectedNetwork === "Select Network") return;
    const codes = new Set(getWithdrawNetworksOrStaticFallback(selectedCoinObj).map((n) => n.code));
    if (!codes.has(selectedNetwork)) {
      setSelectedNetwork("Select Network");
    }
  }, [selectedCoinObj, selectedNetwork]);

  const isWithdrawFormValid = useMemo(() => {
    if (!selectedCoinObj) return false;

    const used24 = Number(withdraw24hUsage?.used);
    const limit24 = Number(withdraw24hUsage?.limit);
    if (Number.isFinite(used24) && Number.isFinite(limit24) && limit24 > 0 && used24 >= limit24) {
      return false;
    }

    const amtRaw = (withdrawAmountInput || "").trim();
    const amtNum = Number.parseFloat(amtRaw.replace(/,/g, ""));
    const amountOk =
      amtRaw.length > 0 &&
      Number.isFinite(amtNum) &&
      amtNum > 0 &&
      amtNum >= minWithdrawForSelection;
    if (!amountOk) return false;

    if (withdrawToTab === "address") {
      const addrOk = withdrawAddress.trim().length > 0;
      const netOk = selectedNetwork !== "Select Network";
      const tokenId =
        selectedCoinObj?.token_asset_ids?.[selectedNetwork] != null
          ? String(selectedCoinObj.token_asset_ids[selectedNetwork]).trim()
          : "";
      const addressValidationOk =
        withdrawAddressValidatedKey ===
        withdrawalAddressValidationKey(selectedNetwork, withdrawAddress.trim(), tokenId);
      return addrOk && netOk && addressValidationOk;
    }

    // AGCX User
    if (agceRecipientTab === "email") {
      const email = agceRecipientEmail.trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }
    if (agceRecipientTab === "phone") {
      const digits = agceRecipientPhone.trim().replace(/\D/g, "");
      return digits.length >= 7 && digits.length <= 15;
    }
    // agce username / uid (input is uncontrolled in UI; allow once user types something meaningful)
    return true;
  }, [
    withdrawAmountInput,
    withdrawToTab,
    withdrawAddress,
    selectedNetwork,
    agceRecipientTab,
    agceRecipientEmail,
    agceRecipientPhone,
    selectedCoinObj,
    withdrawAddressValidatedKey,
    minWithdrawForSelection,
    withdraw24hUsage,
  ]);

  const withdrawAddressInlineError = useMemo(() => {
    if (withdrawToTab !== "address") return "";
    if (!withdrawAddressTouched) return "";
    if (withdrawAddress.trim().length > 0) return "";
    return "Please enter Recipient's Address";
  }, [withdrawAddress, withdrawAddressTouched, withdrawToTab]);

  const withdrawAmountInlineError = useMemo(() => {
    if (!withdrawAmountTouched) return "";
    const raw = (withdrawAmountInput || "").trim();
    const amt = Number.parseFloat(raw.replace(/,/g, ""));
    if (!raw) return "";
    if (!Number.isFinite(amt) || amt <= 0) {
      return `Minimum withdrawal limit is ${formatWithdrawAmountDisplay(minWithdrawForSelection)}.`;
    }
    if (minWithdrawForSelection > 0 && amt < minWithdrawForSelection) {
      return `Minimum withdrawal limit is ${formatWithdrawAmountDisplay(minWithdrawForSelection)}.`;
    }
    if (effectiveAvailable > 0 && amt > effectiveAvailable) return "Insufficient funds";
    return "";
  }, [withdrawAmountInput, withdrawAmountTouched, effectiveAvailable, minWithdrawForSelection]);

  const summaryNetworkLabel = useMemo(() => {
    if (!selectedNetwork || selectedNetwork === "Select Network") return "—";
    const n = activeWithdrawNetworks.find((x) => x.code === selectedNetwork);
    return n ? n.label : selectedNetwork;
  }, [activeWithdrawNetworks, selectedNetwork]);

  // Resolve Fireblocks tokenAssetId for selected network (passed to backend).
  const selectedTokenAssetId = useMemo(() => {
    if (!selectedCoinObj || !selectedNetwork || selectedNetwork === "Select Network") return "";
    return selectedCoinObj.token_asset_ids?.[selectedNetwork] || selectedNetwork;
  }, [selectedCoinObj, selectedNetwork]);

  const validateCurrentWithdrawAddress = useCallback(async () => {
    if (withdrawToTab !== "address") return true;
    const addr = withdrawAddress.trim();
    const net = selectedNetwork && selectedNetwork !== "Select Network" ? selectedNetwork : null;
    if (!addr || !net) return false;
    const validatedKey = withdrawalAddressValidationKey(net, addr, selectedTokenAssetId);
    if (validatedKey && withdrawAddressValidatedKey === validatedKey) {
      setWithdrawAddressValidError("");
      return true;
    }
    const reqId = ++withdrawAddressValidationReqIdRef.current;
    setIsWithdrawAddressValidating(true);
    try {
      const r = await validateWithdrawAddress(addr, net, selectedTokenAssetId || undefined);
      if (r?.success === true && r?.valid === true) {
        if (reqId !== withdrawAddressValidationReqIdRef.current) return false;
        setWithdrawAddressValidError("");
        setWithdrawAddressValidatedKey(validatedKey);
        return true;
      }
      if (r && r.valid === false) {
        if (reqId !== withdrawAddressValidationReqIdRef.current) return false;
        setWithdrawAddressValidError(r.message && r.message !== "ok" ? r.message : `Invalid address for ${net}`);
        setWithdrawAddressValidatedKey("");
        return false;
      }
      if (reqId !== withdrawAddressValidationReqIdRef.current) return false;
      setWithdrawAddressValidError(r?.message && r.message !== "ok" ? r.message : `Invalid address for ${net}`);
      setWithdrawAddressValidatedKey("");
      return false;
    } catch {
      if (reqId !== withdrawAddressValidationReqIdRef.current) return false;
      setWithdrawAddressValidError(`Invalid address for ${net}`);
      setWithdrawAddressValidatedKey("");
      return false;
    } finally {
      if (reqId === withdrawAddressValidationReqIdRef.current) {
        setIsWithdrawAddressValidating(false);
      }
    }
  }, [withdrawToTab, withdrawAddress, selectedNetwork, selectedTokenAssetId, withdrawAddressValidatedKey]);

  const validateCurrentWithdrawAddressRef = useRef(validateCurrentWithdrawAddress);
  validateCurrentWithdrawAddressRef.current = validateCurrentWithdrawAddress;

  useEffect(() => {
    if (withdrawToTab !== "address") return undefined;
    const addr = withdrawAddress.trim();
    const net = selectedNetwork && selectedNetwork !== "Select Network" ? selectedNetwork : null;
    if (!addr || !net) return undefined;

    const validatedKey = withdrawalAddressValidationKey(net, addr, selectedTokenAssetId);
    if (validatedKey && withdrawAddressValidatedKey === validatedKey) {
      return undefined;
    }

    if (withdrawAddrValidateDebounceTimerRef.current) {
      clearTimeout(withdrawAddrValidateDebounceTimerRef.current);
      withdrawAddrValidateDebounceTimerRef.current = null;
    }

    withdrawAddrValidateDebounceTimerRef.current = setTimeout(() => {
      withdrawAddrValidateDebounceTimerRef.current = null;
      void validateCurrentWithdrawAddressRef.current?.();
    }, 500);

    return () => {
      if (withdrawAddrValidateDebounceTimerRef.current) {
        clearTimeout(withdrawAddrValidateDebounceTimerRef.current);
        withdrawAddrValidateDebounceTimerRef.current = null;
      }
    };
  }, [withdrawToTab, withdrawAddress, selectedNetwork, selectedTokenAssetId, withdrawAddressValidatedKey]);

  const invalidateSaveAddrAddressValidation = useCallback(() => {
    saveAddrValidateReqIdRef.current += 1;
    if (saveAddrValidateDebounceTimerRef.current) {
      clearTimeout(saveAddrValidateDebounceTimerRef.current);
      saveAddrValidateDebounceTimerRef.current = null;
    }
    setSaveAddrAddressValidError("");
    setSaveAddrAddressValidatedKey("");
    setSaveAddrAddressValidating(false);
  }, []);

  useEffect(() => {
    invalidateSaveAddrAddressValidation();
  }, [saveAddrCoin, saveAddrNetwork, invalidateSaveAddrAddressValidation]);

  const clearSaveAddrAddressValidationForEdit = useCallback(() => {
    setSaveAddrAddressValidError("");
    setSaveAddrAddressValidatedKey("");
    if (saveAddrValidateDebounceTimerRef.current) {
      clearTimeout(saveAddrValidateDebounceTimerRef.current);
      saveAddrValidateDebounceTimerRef.current = null;
    }
  }, []);

  const validateSaveAddrBookAddress = useCallback(async () => {
    const addr = saveAddrValue.trim();
    const net = saveAddrNetwork && saveAddrNetwork !== "Select Network" ? saveAddrNetwork : null;
    if (!addr || !net || !saveAddrCoin) {
      setSaveAddrAddressValidError("");
      setSaveAddrAddressValidatedKey("");
      setSaveAddrAddressValidating(false);
      return false;
    }
    const vk = withdrawalAddressValidationKey(net, addr, saveAddrTokenAssetId);
    if (!vk) {
      setSaveAddrAddressValidating(false);
      return false;
    }
    if (saveAddrAddressValidatedKey === vk) {
      setSaveAddrAddressValidError("");
      setSaveAddrAddressValidating(false);
      return true;
    }
    const reqId = ++saveAddrValidateReqIdRef.current;
    setSaveAddrAddressValidating(true);
    try {
      const r = await validateWithdrawAddress(addr, net, saveAddrTokenAssetId || undefined);
      if (r?.success === true && r?.valid === true) {
        if (reqId !== saveAddrValidateReqIdRef.current) return false;
        setSaveAddrAddressValidError("");
        setSaveAddrAddressValidatedKey(vk);
        return true;
      }
      if (r && r.valid === false) {
        if (reqId !== saveAddrValidateReqIdRef.current) return false;
        setSaveAddrAddressValidatedKey("");
        setSaveAddrAddressValidError(
          r.message && r.message !== "ok" ? r.message : `Invalid address for ${net}`
        );
        return false;
      }
      if (reqId !== saveAddrValidateReqIdRef.current) return false;
      setSaveAddrAddressValidatedKey("");
      setSaveAddrAddressValidError(
        r?.message && r.message !== "ok" ? r.message : `Invalid address for ${net}`
      );
      return false;
    } catch {
      if (reqId !== saveAddrValidateReqIdRef.current) return false;
      setSaveAddrAddressValidatedKey("");
      setSaveAddrAddressValidError(`Invalid address for ${net}`);
      return false;
    } finally {
      if (reqId === saveAddrValidateReqIdRef.current) {
        setSaveAddrAddressValidating(false);
      }
    }
  }, [
    saveAddrValue,
    saveAddrNetwork,
    saveAddrCoin,
    saveAddrTokenAssetId,
    saveAddrAddressValidatedKey,
  ]);

  const validateSaveAddrBookAddressRef = useRef(validateSaveAddrBookAddress);
  useLayoutEffect(() => {
    validateSaveAddrBookAddressRef.current = validateSaveAddrBookAddress;
  }, [validateSaveAddrBookAddress]);

  useEffect(() => {
    if (!isSaveAddressOpen || saveAddrWizardStep !== SAVE_ADDR_WIZARD_STEP.BASICS) return undefined;
    const addr = saveAddrValue.trim();
    const net = saveAddrNetwork && saveAddrNetwork !== "Select Network" ? saveAddrNetwork : null;
    if (!addr || !net || !saveAddrCoin) return undefined;

    const validatedKey = withdrawalAddressValidationKey(net, addr, saveAddrTokenAssetId);
    if (validatedKey && saveAddrAddressValidatedKey === validatedKey) {
      return undefined;
    }

    if (saveAddrValidateDebounceTimerRef.current) {
      clearTimeout(saveAddrValidateDebounceTimerRef.current);
      saveAddrValidateDebounceTimerRef.current = null;
    }

    saveAddrValidateDebounceTimerRef.current = setTimeout(() => {
      saveAddrValidateDebounceTimerRef.current = null;
      void validateSaveAddrBookAddressRef.current?.();
    }, 500);

    return () => {
      if (saveAddrValidateDebounceTimerRef.current) {
        clearTimeout(saveAddrValidateDebounceTimerRef.current);
        saveAddrValidateDebounceTimerRef.current = null;
      }
    };
  }, [
    isSaveAddressOpen,
    saveAddrWizardStep,
    saveAddrValue,
    saveAddrNetwork,
    saveAddrCoin,
    saveAddrTokenAssetId,
    saveAddrAddressValidatedKey,
  ]);

  const applyWithdrawalAddressHistoryEntry = useCallback((entry) => {
    const addr = String(entry?.address || "").trim();
    if (!addr) return;

    setWithdrawAddress(addr);
    setWithdrawAddressTouched(true);
    setWithdrawAddressValidError("");
    setWithdrawAddressValidatedKey("");

    setShowAddressBookPanel(false);
    window.setTimeout(() => {
      void validateCurrentWithdrawAddressRef.current?.();
    }, 0);
  }, []);

  const withdrawalAddressIsApprovedInBook = useMemo(() => {
    if (withdrawToTab !== "address") return true;
    if (addressBookLoading) return true;
    const addr = withdrawAddress.trim();
    if (!addr) return false;

    // Match saved address book by address string only (same wallet can be reused across assets/networks).
    return addressBookEntries.some((e) => {
      if (!addressBookEntryWithdrawable(e)) return false;
      return addressesEqualForWithdrawalBookMatch(e.address, addr);
    });
  }, [withdrawToTab, addressBookLoading, addressBookEntries, withdrawAddress]);

  // Step 3: network chip, 24h limit row, fee & receive from selected network + amount.
  const withdrawStep3Preview = useMemo(() => {
    if (!selectedCoinObj || !selectedNetwork || selectedNetwork === "Select Network") {
      return {
        networkCode: "—",
        limit24hLine: "— / —",
        limitLeft: "—",
        limitRight: "—",
        feeNum: null,
        receiveNum: null,
      };
    }
    const net = activeWithdrawNetworks.find((n) => n.code === selectedNetwork);
    const feeNum = net?.fee != null ? net.fee : null;
    const amt = parseFloat(String(withdrawAmountInput || "").replace(/,/g, ""));
    const receiveNum =
      feeNum != null && Number.isFinite(amt) && amt > 0 ? Math.max(0, amt - feeNum) : null;
    const remaining = Number(withdraw24hUsage?.remaining);
    const limit = Number(withdraw24hUsage?.limit);
    const usageLine =
      Number.isFinite(remaining) && Number.isFinite(limit)
        ? `${formatWithdrawAmountDisplay(remaining)} / ${formatWithdrawAmountDisplay(limit)}`
        : "— / —";
    const fallbackLine = `${formatWithdrawAmountDisplay(net?.max != null ? Number(net.max) : null)} / ${formatWithdrawAmountDisplay(
      net?.max != null ? Number(net.max) : null
    )}`;
    const limit24hLine = usageLine !== "— / —" ? usageLine : fallbackLine;
    const parts = String(limit24hLine || "— / —").split("/");
    const limitLeft = (parts[0] || "—").trim();
    const limitRight = (parts[1] || "—").trim();
    return {
      networkCode: selectedNetwork,
      limit24hLine,
      limitLeft,
      limitRight,
      feeNum,
      receiveNum,
    };
  }, [
    selectedCoinObj,
    selectedNetwork,
    activeWithdrawNetworks,
    withdrawAmountInput,
    withdraw24hUsage,
  ]);

  // Enabled OTP / fund-password methods (excludes passkey). Used when passkey_enabled is false.
  const getNonPasskeyEnabledMethods = useCallback(() => {
    const s = withdrawSecuritySettings;
    if (!s) return ["email"];
    if (!s.methods) return [];
    const methods = [];
    if (s.methods.email?.enabled) methods.push("email");
    if (s.methods.mobile?.enabled) methods.push("mobile");
    if (s.methods.google_authenticator?.enabled) methods.push("google_authenticator");
    if (s.methods.fund_password?.enabled) methods.push("fund_password");
    return methods;
  }, [withdrawSecuritySettings]);

  // Real non-passkey methods from settings (not the pre-settings `["email"]` default).
  const hasConfigurableNonPasskeyMethods = useMemo(() => {
    const s = withdrawSecuritySettings;
    if (!s?.methods) return false;
    return !!(
      s.methods.email?.enabled ||
      s.methods.mobile?.enabled ||
      s.methods.google_authenticator?.enabled ||
      s.methods.fund_password?.enabled
    );
  }, [withdrawSecuritySettings]);

  const withdrawCombinedVerificationReady = useMemo(() => {
    const methods = getNonPasskeyEnabledMethods();
    if (!methods.length) return false;
    if (methods.includes("email") && !emailVerifyCode.trim()) return false;
    if (methods.includes("mobile") && !String(mobileVerifyCode || "").trim()) return false;
    if (methods.includes("google_authenticator") && !authAppCode.trim()) return false;
    if (methods.includes("fund_password") && !withdrawFundPassword.trim()) return false;
    return true;
  }, [getNonPasskeyEnabledMethods, emailVerifyCode, mobileVerifyCode, authAppCode, withdrawFundPassword]);

  const sendSingleWithdrawalOtp = useCallback(async (method) => {
    if (method !== "email" && method !== "mobile") return;
    setWithdrawOtpSending((p) => ({ ...p, [method]: true }));
    try {
      await sendWithdrawalVerificationOtp(method);
      setWithdrawOtpResendSec((p) => ({ ...p, [method]: WITHDRAW_OTP_RESEND_COOLDOWN_S }));
    } catch {
      alertErrorMessage(method === "email" ? "Could not send email code." : "Could not send SMS code.");
    } finally {
      setWithdrawOtpSending((p) => ({ ...p, [method]: false }));
    }
  }, []);

  const withdrawOtpResendActive = withdrawOtpResendSec.email > 0 || withdrawOtpResendSec.mobile > 0;
  useEffect(() => {
    if (!withdrawOtpResendActive) return undefined;
    const id = window.setInterval(() => {
      setWithdrawOtpResendSec((prev) => ({
        email: prev.email > 0 ? prev.email - 1 : 0,
        mobile: prev.mobile > 0 ? prev.mobile - 1 : 0,
      }));
    }, 1000);
    return () => window.clearInterval(id);
  }, [withdrawOtpResendActive]);

  const addrBookOtpResendActive = addrBookOtpResendSec.email > 0 || addrBookOtpResendSec.mobile > 0;
  useEffect(() => {
    if (!addrBookOtpResendActive) return undefined;
    const id = window.setInterval(() => {
      setAddrBookOtpResendSec((prev) => ({
        email: prev.email > 0 ? prev.email - 1 : 0,
        mobile: prev.mobile > 0 ? prev.mobile - 1 : 0,
      }));
    }, 1000);
    return () => window.clearInterval(id);
  }, [addrBookOtpResendActive]);

  const sendAddressBookSingleOtp = useCallback(async (method) => {
    if (method !== "email" && method !== "mobile") return;
    setAddrBookOtpSending((p) => ({ ...p, [method]: true }));
    try {
      const res = await sendAddressBookVerificationOtp({ method });
      if (res?.success === false) {
        alertErrorMessage(res?.message || "Could not send code.");
        return;
      }
      if (res?.message) alertSuccessMessage(res.message);
      setAddrBookOtpResendSec((p) => ({ ...p, [method]: WITHDRAW_OTP_RESEND_COOLDOWN_S }));
    } catch {
      alertErrorMessage(method === "email" ? "Could not send email code." : "Could not send SMS code.");
    } finally {
      setAddrBookOtpSending((p) => ({ ...p, [method]: false }));
    }
  }, []);

  const resetWithdrawalFormAfterSuccessfulSubmit = useCallback(() => {
    setWithdrawAmountInput("");
    setWithdrawAmountTouched(false);
    setWithdrawAddress("");
    setWithdrawAddressTouched(false);
    setWithdrawAddressValidError("");
    setWithdrawAddressValidatedKey("");
    withdrawAddressValidationReqIdRef.current += 1;
    setShowAddressBookPanel(false);
  }, []);

  // closeAllOverlays is component-scoped; keeping stable behavior for submission side effects.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const submitWithdrawalWithEnabledMethods = useCallback(
    async (overrides = {}) => {
      if (withdrawalRequestLockRef.current) return;
      if (withdrawToTab !== "address") return;
      if (selectedNetwork === "Select Network" || !withdrawAddress.trim()) {
        alertErrorMessage("Address and network are required.");
        return;
      }
      const isAddressValid = await validateCurrentWithdrawAddress();
      if (!isAddressValid) {
        setWithdrawAddressTouched(true);
        alertErrorMessage(withdrawAddressValidError || "Please enter a valid withdrawal address.");
        return;
      }
      if (!selectedCoinObj) {
        alertErrorMessage("Please select a coin.");
        return;
      }
      if (!selectedTokenAssetId) {
        alertErrorMessage("Please select a network.");
        return;
      }
      const amount = parseFloat(String(withdrawAmountInput || "").replace(/,/g, ""));
      if (!Number.isFinite(amount) || amount <= 0 || amount < minWithdrawForSelection) {
        alertErrorMessage(`Minimum withdrawal limit is ${formatWithdrawAmountDisplay(minWithdrawForSelection)}.`);
        return;
      }
      if (effectiveAvailable > 0 && amount > effectiveAvailable) {
        alertErrorMessage("Insufficient funds");
        return;
      }

      const otpMethods = getNonPasskeyEnabledMethods();
      const usePasskeyPath =
        withdrawSecuritySettings?.passkey_enabled === true && !withdrawOtpFallbackAfterPasskey;

      if (!usePasskeyPath && !otpMethods.length) {
        setIsNoMethodsModalOpen(true);
        return;
      }

      const used24 = Number(withdraw24hUsage?.used);
      const limit24 = Number(withdraw24hUsage?.limit);
      if (Number.isFinite(used24) && Number.isFinite(limit24) && limit24 > 0 && used24 >= limit24) {
        alertErrorMessage(mapWithdrawError({ code: "DAILY_LIMIT_EXCEEDED" }));
        return;
      }

      const emailCode = String(overrides.email_code ?? emailVerifyCode ?? "").trim();
      const mobileCode = String(overrides.mobile_code ?? mobileVerifyCode ?? "").trim();
      const googleCode = String(overrides.google_authenticator_code ?? authAppCode ?? "").trim();
      const fundPassword = String(overrides.fund_password ?? withdrawFundPassword ?? "").trim();

      if (usePasskeyPath) {
        if (!overrides.passkey_credential) {
          alertErrorMessage("Complete passkey verification to continue.");
          return;
        }
      } else {
        if (otpMethods.includes("email") && !emailCode) {
          setIsWithdrawVerifyCombinedOpen(true);
          alertErrorMessage("Please enter the email OTP.");
          return;
        }
        if (otpMethods.includes("mobile") && !mobileCode) {
          setIsWithdrawVerifyCombinedOpen(true);
          alertErrorMessage("Please enter the mobile OTP.");
          return;
        }
        if (otpMethods.includes("google_authenticator") && !googleCode) {
          setIsWithdrawVerifyCombinedOpen(true);
          alertErrorMessage("Please enter the authenticator app code.");
          return;
        }
        if (otpMethods.includes("fund_password") && !fundPassword) {
          setIsWithdrawVerifyCombinedOpen(true);
          setWithdrawFundPasswordError("Please enter your fund password.");
          return;
        }
      }

      withdrawalRequestLockRef.current = true;
      setWithdrawSubmitting(true);
      setWithdrawFundPasswordError("");
      setWithdrawVerifyFieldErrors({ email: "", mobile: "", google_authenticator: "", fund_password: "" });
      try {
        const body = {
          coinName: selectedCoin,
          tokenAssetId: selectedTokenAssetId,
          withdrawal_address: withdrawAddress.trim(),
          amount,
        };
        if (usePasskeyPath) {
          body.passkey_credential = overrides.passkey_credential;
        } else {
          if (otpMethods.includes("email")) body.email_code = emailCode;
          if (otpMethods.includes("mobile")) body.mobile_code = mobileCode;
          if (otpMethods.includes("google_authenticator")) body.google_authenticator_code = googleCode;
          if (otpMethods.includes("fund_password")) body.fund_password = fundPassword;
        }

        const res = await submitWithdrawal(body);
        const ok = res && res.success !== false && res?.message !== "Token is expired";
        if (ok) {
          const created = res?.data && typeof res.data === "object" ? res.data : {};
          const needsDeclaration =
            created.declaration_required === true ||
            String(created.status || "").toUpperCase() === "AWAITING_DECLARATION";
          const declarationId = created.transactionId || created.withdrawal_id || "";
          if (needsDeclaration && declarationId) {
            closeAllOverlays();
            setAuthAppCode("");
            setEmailVerifyCode("");
            setMobileVerifyCode("");
            setWithdrawFundPassword("");
            setWithdrawFundPasswordVisible(false);
            setWithdrawVerifyFieldErrors({ email: "", mobile: "", google_authenticator: "", fund_password: "" });
            setWithdrawOtpFallbackAfterPasskey(false);
            resetWithdrawalFormAfterSuccessfulSubmit();
            await Promise.all([
              refetchWithdrawHistory(),
              refetchWithdrawalAddressHistory(),
              reloadWithdrawFunds(),
              reloadWithdrawal24hUsage(),
            ]);
            navigate(withdrawalDeclarationPath(declarationId));
            return;
          }
          const awaiting = res?.data?.awaiting_admin !== false;
          const travelRule = String(res?.data?.status || "").toUpperCase() === "TRAVEL_RULE_REVIEW";
          alertSuccessMessage(
            res?.message ||
              res?.data?.message ||
              (travelRule
                ? "Withdrawal held for compliance review."
                : awaiting
                  ? "Withdrawal requested. Waiting for admin approval."
                  : "Withdrawal submitted.")
          );
          closeAllOverlays();
          setAuthAppCode("");
          setEmailVerifyCode("");
          setMobileVerifyCode("");
          setWithdrawFundPassword("");
          setWithdrawFundPasswordVisible(false);
          setWithdrawVerifyFieldErrors({ email: "", mobile: "", google_authenticator: "", fund_password: "" });
          setWithdrawOtpFallbackAfterPasskey(false);
          resetWithdrawalFormAfterSuccessfulSubmit();
          await Promise.all([
            refetchWithdrawHistory(),
            refetchWithdrawalAddressHistory(),
            reloadWithdrawFunds(),
            reloadWithdrawal24hUsage(),
          ]);
          setIsAwaitingApprovalOpen(true);
          return;
        }

        const failed = res?.failed_method;
        const nextFieldErrors = { email: "", mobile: "", google_authenticator: "", fund_password: "" };
        if (failed === "mobile" || res?.code === "MOBILE_OTP_INVALID") {
          nextFieldErrors.mobile = res?.message || "";
        } else if (failed === "email" || res?.code === "EMAIL_OTP_INVALID") {
          nextFieldErrors.email = res?.message || "";
        } else if (res?.code === "OTP_EXPIRED") {
          if (failed === "mobile") nextFieldErrors.mobile = res?.message || "";
          else nextFieldErrors.email = res?.message || "";
        } else if (failed === "google_authenticator" || res?.code === "GOOGLE_AUTH_INVALID") {
          nextFieldErrors.google_authenticator = res?.message || "";
        } else if (failed === "fund_password" || res?.code === "FUND_PASSWORD_INVALID") {
          nextFieldErrors.fund_password = res?.message || "";
          setWithdrawFundPasswordError(res?.message || "Invalid fund password");
        }
        if (Object.values(nextFieldErrors).some(Boolean)) {
          setWithdrawVerifyFieldErrors(nextFieldErrors);
          if (!usePasskeyPath) setIsWithdrawVerifyCombinedOpen(true);
        }

        if (res?.code === "NO_VERIFICATION_METHOD") {
          setIsNoMethodsModalOpen(true);
        } else if (String(res?.code || "").startsWith("PASSKEY_")) {
          setWithdrawOtpFallbackAfterPasskey(false);
          setIsPasskeyOpen(true);
        }

        alertErrorMessage(res?.message || mapWithdrawError(res));
      } catch (e) {
        alertErrorMessage(e?.message || "Withdrawal failed");
      } finally {
        withdrawalRequestLockRef.current = false;
        setWithdrawSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      withdrawToTab,
      selectedNetwork,
      withdrawAddress,
      validateCurrentWithdrawAddress,
      withdrawAddressValidError,
      selectedCoinObj,
      withdrawAmountInput,
      minWithdrawForSelection,
      effectiveAvailable,
      withdrawSecuritySettings,
      withdrawOtpFallbackAfterPasskey,
      withdraw24hUsage,
      getNonPasskeyEnabledMethods,
      emailVerifyCode,
      mobileVerifyCode,
      authAppCode,
      withdrawFundPassword,
      selectedCoin,
      selectedTokenAssetId,
      refetchWithdrawHistory,
      refetchWithdrawalAddressHistory,
      reloadWithdrawFunds,
      reloadWithdrawal24hUsage,
      navigate,
    ]
  );

  const handlePasskeyWithdrawFlow = useCallback(async () => {
    try {
      const raw = await fetchPasskeyWithdrawalChallenge();
      const d = raw?.data ?? raw;
      const challenge = d?.challenge;
      const credentialIds = Array.isArray(d?.credentialIds) ? d.credentialIds : [];
      if (!challenge || raw?.success === false) {
        alertErrorMessage(raw?.message || "Could not start passkey verification.");
        return;
      }
      const allowCredentials =
        credentialIds.length > 0
          ? credentialIds.map((id) => ({ id, type: "public-key" }))
          : undefined;
      let credential;
      try {
        credential = await startAuthentication({
          optionsJSON: {
            challenge,
            ...(allowCredentials ? { allowCredentials } : {}),
            userVerification: "preferred",
            timeout: 60000,
          },
        });
      } catch (webauthnError) {
        if (webauthnError?.name === "NotAllowedError") {
          alertErrorMessage("Authentication was cancelled or timed out.");
        } else {
          alertErrorMessage(webauthnError?.message || "Passkey verification failed.");
        }
        return;
      }
      await submitWithdrawalWithEnabledMethods({ passkey_credential: credential });
    } catch (e) {
      alertErrorMessage(e?.message || "Passkey verification failed.");
    }
  }, [submitWithdrawalWithEnabledMethods]);

  const handleWithdrawalSubmitFromCode = useCallback(
    async (code) => {
      const c = String(code || "").trim();
      if (!c) {
        alertErrorMessage("Please enter the verification code.");
        return;
      }
      if (withdrawVerifyMethod === "mobile") {
        await submitWithdrawalWithEnabledMethods({ mobile_code: c });
      } else if (withdrawVerifyMethod === "google_authenticator") {
        await submitWithdrawalWithEnabledMethods({ google_authenticator_code: c });
      } else {
        await submitWithdrawalWithEnabledMethods({ email_code: c });
      }
    },
    [submitWithdrawalWithEnabledMethods, withdrawVerifyMethod]
  );

  // Submit withdrawal with fund password as one of the enabled methods.
  const handleWithdrawalSubmitFundPassword = useCallback(async () => {
    const fp = withdrawFundPassword.trim();
    if (!fp) {
      setWithdrawFundPasswordError("Please enter your fund password.");
      return;
    }
    await submitWithdrawalWithEnabledMethods({ fund_password: fp });
  }, [withdrawFundPassword, submitWithdrawalWithEnabledMethods]);

  // Open withdrawal verification flow:
  // - passkey enabled => passkey-only modal
  // - otherwise combined verification modal; user taps Send code for email/SMS OTPs (no auto-send).
  const openWithdrawVerificationModal = useCallback(() => {
    const passkeyOnly = withdrawSecuritySettings?.passkey_enabled === true;
    const methods = getNonPasskeyEnabledMethods();
    setWithdrawOtpFallbackAfterPasskey(false);
    if (passkeyOnly) {
      setVerificationContext("withdraw");
      setWithdrawVerifyMethod("passkey");
      setWithdrawVerifyFieldErrors({ email: "", mobile: "", google_authenticator: "", fund_password: "" });
      setIsPasskeyOpen(true);
      return;
    }
    if (!methods.length) {
      setIsNoMethodsModalOpen(true);
      return;
    }
    setVerificationContext("withdraw");
    setWithdrawOtpSending({ email: false, mobile: false });
    setWithdrawOtpResendSec({ email: 0, mobile: 0 });
    setWithdrawVerifyFieldErrors({ email: "", mobile: "", google_authenticator: "", fund_password: "" });
    setIsWithdrawVerifyCombinedOpen(true);
  }, [withdrawSecuritySettings, getNonPasskeyEnabledMethods]);

  // Use OTP / fund password after passkey modal.
  const openWithdrawOtpFallbackFromPasskey = useCallback(() => {
    setWithdrawOtpFallbackAfterPasskey(true);
    setIsPasskeyOpen(false);
    setWithdrawOtpSending({ email: false, mobile: false });
    setWithdrawOtpResendSec({ email: 0, mobile: 0 });
    setWithdrawVerifyFieldErrors({ email: "", mobile: "", google_authenticator: "", fund_password: "" });
    const methods = getNonPasskeyEnabledMethods();
    if (!methods.length) {
      setIsNoMethodsModalOpen(true);
      return;
    }
    setIsWithdrawVerifyCombinedOpen(true);
  }, [getNonPasskeyEnabledMethods]);

  // Open combined OTP modal without re-sending codes (e.g. “Switch verification method”).
  const openWithdrawVerificationCombinedOnly = useCallback(() => {
    if (withdrawSecuritySettings?.passkey_enabled === true && !withdrawOtpFallbackAfterPasskey) return;
    const methods = getNonPasskeyEnabledMethods();
    if (!methods.length) {
      setIsNoMethodsModalOpen(true);
      return;
    }
    setVerificationContext("withdraw");
    setIsEmailVerifyOpen(false);
    setIsMobileVerifyOpen(false);
    setIsAuthAppOpen(false);
    setIsWithdrawVerifyCombinedOpen(true);
  }, [withdrawSecuritySettings, withdrawOtpFallbackAfterPasskey, getNonPasskeyEnabledMethods]);

  const shouldShowPasskeyLink = useCallback(
    (currentMethod) =>
      verificationContext !== "withdraw" &&
      verificationContext !== "address_book" &&
      currentMethod !== "passkey" &&
      withdrawSecuritySettings?.passkey_enabled === true,
    [verificationContext, withdrawSecuritySettings]
  );

  const shouldShowSwitchMethodLink = useCallback(
    (_currentMethod) => {
      if (verificationContext === "address_book") return false;
      if (verificationContext !== "withdraw") return false;
      if (withdrawSecuritySettings?.passkey_enabled === true && !withdrawOtpFallbackAfterPasskey) return false;
      if (isWithdrawVerifyCombinedOpen) return false;
      return getNonPasskeyEnabledMethods().length > 1;
    },
    [
      verificationContext,
      withdrawSecuritySettings,
      withdrawOtpFallbackAfterPasskey,
      getNonPasskeyEnabledMethods,
      isWithdrawVerifyCombinedOpen,
    ]
  );

  const openSecurityUnavailableFlow = () => {
    closeAllOverlays();
    navigate("/asset_managemnet/security_verification_unavailable");
  };

  const openPasskeysOnlyRequirements = () => {
    closeAllOverlays();
    setVerificationContext("reset");
    setSecUnavailable({ email: false, phone: false, authApp: false, passkeys: true });
    setSecReqDone({ authApp: false, email: false, phone: false, passkeys: false });
    setIsSecResetConfirmOpen(true);
  };

  const secUnavailableSelectedCount = useMemo(
    () => Object.values(secUnavailable).filter(Boolean).length,
    [secUnavailable]
  );

  const secRequirementsMode = useMemo(() => {
    if (secUnavailable.passkeys && secUnavailableSelectedCount === 1) return "passkeys_only";
    return "standard";
  }, [secUnavailable.passkeys, secUnavailableSelectedCount]);

  const secReqTotal = secRequirementsMode === "passkeys_only" ? 1 : 2;

  const secReqCompleted = useMemo(() => {
    if (secRequirementsMode === "passkeys_only") {
      return Number(secReqDone.passkeys);
    }
    const second =
      (secUnavailable.phone ? secReqDone.phone : false) || (secUnavailable.email ? secReqDone.email : false);
    return Number(secReqDone.authApp) + Number(second);
  }, [secReqDone, secRequirementsMode, secUnavailable.email, secUnavailable.phone]);

  const agceErrors = useMemo(() => {
    const errs = { email: "", phone: "" };

    const email = agceRecipientEmail.trim();
    if (agceTouched.email) {
      if (!email) errs.email = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Please enter a valid email";
    }

    const phone = agceRecipientPhone.trim();
    if (agceTouched.phone) {
      if (!phone) errs.phone = "Phone number is required";
      else {
        const digits = phone.replace(/\D/g, "");
        if (digits.length < 7 || digits.length > 15) errs.phone = "Please enter a valid phone number";
      }
    }

    return errs;
  }, [agceRecipientEmail, agceRecipientPhone, agceTouched.email, agceTouched.phone]);

  const handleAgceEmailChange = (e) => {
    const v = e.target.value;
    setAgceRecipientEmail(v);
    const t = v.trim();
    if (!t) setAgceTouched((p) => ({ ...p, email: false }));
  };

  const handleAgcePhoneChange = (e) => {
    const v = e.target.value;
    setAgceRecipientPhone(v);
    const t = v.trim();
    if (!t) setAgceTouched((p) => ({ ...p, phone: false }));
  };

  const handleWithdrawMaxClick = () => {
    if (!selectedCoinObj) return;
    if (effectiveAvailable > 0) {
      setWithdrawAmountInput(sanitizeWithdrawAmountRaw(String(effectiveAvailable)));
      setWithdrawAmountTouched(true);
    }
  };

  const handleWithdrawAmountChange = (e) => {
    setWithdrawAmountInput(sanitizeWithdrawAmountRaw(e.target.value));
    if (!withdrawAmountTouched) setWithdrawAmountTouched(true);
  };

  const copyWithdrawText = async (text, successMsg = "Copied") => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      alertSuccessMessage(successMsg);
    } catch {
      /* ignore */
    }
  };

  const pasteWithdrawAuthAppCode = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      const digits = String(text ?? "").replace(/\D/g, "").slice(0, WITHDRAW_VERIFY_CODE_LEN);
      setAuthAppCode(digits);
      setWithdrawVerifyFieldErrors((p) => ({ ...p, google_authenticator: "" }));
      if (!digits) {
        alertErrorMessage("Clipboard did not contain a code to paste.");
      }
    } catch {
      alertErrorMessage("Could not read clipboard. Paste manually or allow clipboard access.");
    }
  }, []);

  const onWithdrawAgainFromHistory = () => {
    const row = selectedWithdrawHistoryRow;
    if (row) {
      const rowCoin = String(row.coin || "").trim().toUpperCase();
      const rowNetwork = String(row.network || "").trim().toUpperCase();
      const rowTokenAssetId = String(row.tokenAssetId || "").trim().toUpperCase();
      const rowAddress = String(row.addressFull || "").trim();
      const aliasMap = {
        BEP20: "BSC",
        ERC20: "ETH",
        TRC20: "TRX",
      };

      if (rowCoin) {
        const coinMatch = withdrawCoins.find((c) => coinSymbol(c).toUpperCase() === rowCoin);
        if (coinMatch) setSelectedCoin(rowCoin);
      }
      setWithdrawToTab("address");
      setWithdrawAddress(rowAddress);
      setWithdrawAddressTouched(Boolean(rowAddress));
      setWithdrawAddressValidError("");
      setWithdrawAddressValidatedKey("");

      if (rowCoin && rowNetwork) {
        const coinForNetwork = withdrawCoins.find((c) => coinSymbol(c).toUpperCase() === rowCoin);
        const netCodes = coinForNetwork ? getWithdrawNetworksOrStaticFallback(coinForNetwork).map((n) => n.code) : [];
        const normalizedNetwork = aliasMap[rowNetwork] || rowNetwork;
        let resolvedNetwork = netCodes.includes(normalizedNetwork) ? normalizedNetwork : "";
        if (!resolvedNetwork && rowTokenAssetId && coinForNetwork?.token_asset_ids) {
          const byTokenAsset = Object.entries(coinForNetwork.token_asset_ids).find(
            ([, v]) => String(v || "").trim().toUpperCase() === rowTokenAssetId
          );
          if (byTokenAsset?.[0] && netCodes.includes(byTokenAsset[0])) {
            resolvedNetwork = byTokenAsset[0];
          }
        }
        setSelectedNetwork(resolvedNetwork || "Select Network");
      }
    }
    setIsWithdrawHistoryDetailOpen(false);
    window.requestAnimationFrame(() => {
      document.querySelector(".deposit_crypto_left .deposit_step_section.select_coin_option")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const cancelPendingWithdrawal = useCallback(
    async (row) => {
      const target = row || selectedWithdrawHistoryRow;
      if (!target?.canCancel) {
        alertErrorMessage("This withdrawal can no longer be cancelled.");
        return;
      }
      const _id = target.id;
      const currency_id = target.currencyId;
      if (!_id || currency_id == null || currency_id === "") {
        alertErrorMessage("Cannot cancel: missing withdrawal id.");
        return;
      }
      if (withdrawCancelBusy) return;
      setWithdrawCancelBusy(true);
      try {
        const res = await cancelUserWithdrawal({ _id, currency_id });
        if (res?.success) {
          alertSuccessMessage(res?.message || "Withdrawal cancelled. Funds returned to your wallet.");
          setIsWithdrawHistoryDetailOpen(false);
          setSelectedWithdrawHistoryRow(null);
          await Promise.all([refetchWithdrawHistory(), reloadWithdrawFunds(), reloadWithdrawal24hUsage()]);
          return;
        }
        alertErrorMessage(res?.message || "Could not cancel this withdrawal.");
      } catch (e) {
        alertErrorMessage(e?.message || "Could not cancel this withdrawal.");
      } finally {
        setWithdrawCancelBusy(false);
      }
    },
    [
      selectedWithdrawHistoryRow,
      withdrawCancelBusy,
      refetchWithdrawHistory,
      reloadWithdrawFunds,
      reloadWithdrawal24hUsage,
    ]
  );

  const saveAddrBasicsRemoteBlock = useMemo(() => {
    const addr = saveAddrValue.trim();
    const net = saveAddrNetwork;
    const coin = saveAddrCoin;
    if (!addr || !net || !coin) return false;
    const vk = withdrawalAddressValidationKey(net, addr, saveAddrTokenAssetId);
    if (!vk) return false;
    return (
      saveAddrAddressValidating ||
      saveAddrAddressValidatedKey !== vk ||
      Boolean(saveAddrAddressValidError)
    );
  }, [
    saveAddrValue,
    saveAddrNetwork,
    saveAddrCoin,
    saveAddrTokenAssetId,
    saveAddrAddressValidating,
    saveAddrAddressValidatedKey,
    saveAddrAddressValidError,
  ]);

  const saveAddrErrors = useMemo(() => {
    const errs = {};

    const label = saveAddrLabel.trim();
    if (!label) errs.label = "Address label is required";
    else if (label.length < 4 || label.length > 20) errs.label = "Address label must be 4-20 characters";

    const addr = saveAddrValue.trim();
    if (!addr) errs.address = "Address is required";

    if (!saveAddrCoin) errs.coin = "Please select coin";
    if (!saveAddrNetwork) errs.network = "Please select network";

    return errs;
  }, [saveAddrLabel, saveAddrValue, saveAddrCoin, saveAddrNetwork]);

  const isSaveAddrValid = useMemo(() => Object.keys(saveAddrErrors).length === 0, [saveAddrErrors]);

  const saveAddrBasicsNextDisabled = useMemo(
    () => !isSaveAddrValid || saveAddrBasicsRemoteBlock,
    [isSaveAddrValid, saveAddrBasicsRemoteBlock]
  );

  const saveAddrOtherIdentityErrors = useMemo(() => {
    const errs = {};
    if (!saveAddrSubmitAttempted || saveAddrWizardStep !== SAVE_ADDR_WIZARD_STEP.OTHER_IDENTITY) return errs;
    if (!saveAddrBenFullName.trim()) errs.fullName = "Full name is required";
    if (!saveAddrBenPan.trim()) errs.pan = "PAN / National ID is required";
    if (!saveAddrBenCountry.trim()) errs.country = "Country of residence is required";
    if (!saveAddrBenPin.trim()) errs.pin = "PIN / Postal code is required";
    const street = saveAddrBenAddress.trim();
    if (!street || street.length < 5) errs.address = "Enter a complete address";
    return errs;
  }, [
    saveAddrSubmitAttempted,
    saveAddrWizardStep,
    saveAddrBenFullName,
    saveAddrBenPan,
    saveAddrBenCountry,
    saveAddrBenPin,
    saveAddrBenAddress,
  ]);

  const saveAddrOwnershipError = useMemo(() => {
    if (!saveAddrSubmitAttempted || saveAddrWizardStep !== SAVE_ADDR_WIZARD_STEP.OWNER) return "";
    if (!saveAddrOwnership) return "Please choose who owns this withdrawal address.";
    return "";
  }, [saveAddrSubmitAttempted, saveAddrWizardStep, saveAddrOwnership]);

  const saveAddrWalletKindSelfError = useMemo(() => {
    if (!saveAddrSubmitAttempted || saveAddrWizardStep !== SAVE_ADDR_WIZARD_STEP.SELF_WALLET_TYPE) return "";
    if (!saveAddrWalletKindSelf) return "Select where this wallet is custodied.";
    return "";
  }, [saveAddrSubmitAttempted, saveAddrWizardStep, saveAddrWalletKindSelf]);

  const saveAddrWalletKindOtherError = useMemo(() => {
    if (!saveAddrSubmitAttempted || saveAddrWizardStep !== SAVE_ADDR_WIZARD_STEP.OTHER_WALLET_TYPE) return "";
    if (!saveAddrWalletKindOther) return "Select how the counterparty custody is held.";
    return "";
  }, [saveAddrSubmitAttempted, saveAddrWizardStep, saveAddrWalletKindOther]);

  const saveAddrExchangeStepErrors = useMemo(() => {
    const errs = {};
    const onStep =
      saveAddrWizardStep === SAVE_ADDR_WIZARD_STEP.SELF_EXCHANGE ||
      saveAddrWizardStep === SAVE_ADDR_WIZARD_STEP.OTHER_EXCHANGE;
    if (!saveAddrSubmitAttempted || !onStep) return errs;
    if (!saveAddrExchangePick) errs.exchange = "Please select an exchange.";
    else if (
      saveAddrExchangePick === ADDRESS_BOOK_EXCHANGE_OTHER &&
      !String(saveAddrExchangeManual || "").trim()
    ) {
      errs.exchange = "Enter the institution name.";
    } else if (
      saveAddrExchangePick !== ADDRESS_BOOK_EXCHANGE_OTHER &&
      !ADDRESS_BOOK_TOP_EXCHANGES.some((x) => x.value === saveAddrExchangePick)
    ) {
      errs.exchange = "Please select a valid exchange.";
    }
    if (!saveAddrDeclarationAccepted)
      errs.declaration = "You must acknowledge the declaration to continue.";
    return errs;
  }, [
    saveAddrSubmitAttempted,
    saveAddrWizardStep,
    saveAddrExchangePick,
    saveAddrExchangeManual,
    saveAddrDeclarationAccepted,
  ]);

  const isSaveAddrSelfVerifyValid = useMemo(() => {
    if (saveAddrWizardStep !== SAVE_ADDR_WIZARD_STEP.SELF_VERIFY) return false;
    if (
      saveAddrWhitelistMethod === "METAMASK" &&
      saveAddrChainCanonical &&
      !isMetaMaskWhitelistChain(saveAddrChainCanonical)
    )
      return false;
    return isSaveAddrValid;
  }, [saveAddrWizardStep, saveAddrWhitelistMethod, saveAddrChainCanonical, isSaveAddrValid]);

  const clearSaveAddrBeneficiaryFields = useCallback(() => {
    setSaveAddrBenFullName("");
    setSaveAddrBenPan("");
    setSaveAddrBenCountry("");
    setSaveAddrBenPin("");
    setSaveAddrBenAddress("");
  }, []);

  const clearSaveAddrExchangeFields = useCallback(() => {
    setSaveAddrExchangePick("");
    setSaveAddrExchangeManual("");
    setSaveAddrDeclarationAccepted(false);
    setSaveExchangeMenuOpen(false);
  }, []);

  const saveAddrCoinDisplay = useMemo(
    () => saveAddrCoinOptionsList.find((o) => o.value === saveAddrCoin)?.label || "",
    [saveAddrCoin, saveAddrCoinOptionsList]
  );
  const saveAddrNetworkDisplay = useMemo(
    () => saveAddrNetworkOptionsEffective.find((o) => o.value === saveAddrNetwork)?.label || "",
    [saveAddrNetwork, saveAddrNetworkOptionsEffective]
  );

  const clearSatoshiWhitelistPoll = useCallback(() => {
    satoshiWhitelistConfirmInFlightRef.current = false;
    setSaveAddrSatoshiPolling(false);
    setSatoshiWhitelistAwaitingProof(false);
  }, []);

  const resetSaveAddressFormFields = useCallback(() => {
    clearSatoshiWhitelistPoll();
    if (saveAddrValidateDebounceTimerRef.current) {
      clearTimeout(saveAddrValidateDebounceTimerRef.current);
      saveAddrValidateDebounceTimerRef.current = null;
    }
    saveAddrValidateReqIdRef.current += 1;
    setSaveAddrAddressValidError("");
    setSaveAddrAddressValidatedKey("");
    setSaveAddrAddressValidating(false);
    setSaveAddrLabel("");
    setSaveAddrCoin("");
    setSaveAddrUniversal(false);
    setSaveAddrValue("");
    setSaveAddrNetwork("");
    setSaveAddrMemo("");
    setSaveAddrWhitelistMethod("SATOSHI");
    setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.BASICS);
    setSaveAddrOwnership(SAVE_ADDR_DEFAULT_OWNERSHIP);
    setSaveAddrWalletKindSelf(SAVE_ADDR_DEFAULT_SELF_WALLET_KIND);
    setSaveAddrWalletKindOther("");
    clearSaveAddrExchangeFields();
    clearSaveAddrBeneficiaryFields();
    setAddressWhitelistFlow(null);
    setSatoshiDepositAddress("");
    setSatoshiDepositMemo("");
    setSatoshiDepositLoading(false);
    setSatoshiDepositError(null);
    setWhitelistMetamaskBusy(false);
    setMetamaskInstallModalOpen(false);
    setSaveAddrSubmitAttempted(false);
    addressBookPendingInitiateBodyRef.current = null;
    addressBookPendingInitiateCoreRef.current = null;
    addressBookVerifyReturnStepRef.current = SAVE_ADDR_WIZARD_STEP.BASICS;
    setAddrBookVerificationOptionsState(null);
    setAddrBookVerificationOptionsLoading(false);
    setAddrBookVerifyStage("idle");
    setAddrBookOtpKind(null);
    setAddrBookOtpBoxes(["", "", "", "", "", ""]);
    setAddrBookOtpBusy(false);
    closeAddressBookVerificationUi();
  }, [
    clearSatoshiWhitelistPoll,
    clearSaveAddrBeneficiaryFields,
    clearSaveAddrExchangeFields,
    closeAddressBookVerificationUi,
  ]);

  const beginAddressBookVerificationGate = useCallback((body, core, returnStep) => {
    addressBookPendingInitiateBodyRef.current = body;
    addressBookPendingInitiateCoreRef.current = core;
    addressBookVerifyReturnStepRef.current = returnStep ?? SAVE_ADDR_WIZARD_STEP.BASICS;
    addressBookVerificationEntryOpenedRef.current = false;
    closeAddressBookVerificationUi();
    setSaveAddrSubmitAttempted(false);
    setAddrBookVerificationOptionsState(null);
    setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.VERIFY_ACCOUNT);
  }, [closeAddressBookVerificationUi]);

  const buildAddressBookWizardCoreFields = useCallback(() => {
    const netOpt = saveAddrNetworkOptionsEffective.find((o) => o.value === saveAddrNetwork);
    const chainLegacy = netOpt?.chainCode || String(saveAddrNetwork || "").trim().toUpperCase();
    const chainCanonical =
      (saveAddrChainCanonical && String(saveAddrChainCanonical).trim()) ||
      canonicalWithdrawalChainFromUiCode(chainLegacy) ||
      String(chainLegacy || "").trim().toUpperCase();
    const tokenAssetIdForApi =
      netOpt?.tokenAssetId != null && String(netOpt.tokenAssetId).trim()
        ? String(netOpt.tokenAssetId).trim()
        : "";
    return {
      netOpt,
      chainLegacy,
      chainCanonical,
      address: String(saveAddrValue || "").trim(),
      short_name: String(saveAddrCoin || "").trim().toUpperCase(),
      label: String(saveAddrLabel || "").trim(),
      memoTrim: String(saveAddrMemo || "").trim(),
      tokenAssetIdForApi,
    };
  }, [
    saveAddrNetworkOptionsEffective,
    saveAddrNetwork,
    saveAddrChainCanonical,
    saveAddrValue,
    saveAddrCoin,
    saveAddrLabel,
    saveAddrMemo,
  ]);

  const otherPersonPayloadFromWizard = useCallback(() => {
    const pin = saveAddrBenPin.trim();
    const addr = saveAddrBenAddress.trim();
    return {
      full_name: saveAddrBenFullName.trim(),
      national_id: saveAddrBenPan.trim(),
      country: saveAddrBenCountry.trim(),
      ...(pin ? { pincode: pin } : {}),
      ...(addr ? { full_address: addr } : {}),
    };
  }, [
    saveAddrBenFullName,
    saveAddrBenPan,
    saveAddrBenCountry,
    saveAddrBenPin,
    saveAddrBenAddress,
  ]);

  const processAddressBookInitiateResponse = useCallback(
    async (res, core) => {
      if (!res || res.success === false) {
        const code = String(res?.code || "")
          .toUpperCase()
          .replace(/-/g, "_");
        if (code === "AML_BLOCKED") {
          alertErrorMessage(res?.message || "This address could not be whitelisted for compliance reasons.");
          return false;
        }
        if (
          code === "VERIFICATION_FAILED" ||
          code === "PASSKEY_VERIFICATION_FAILED" ||
          code === "PASSKEY_CHALLENGE_EXPIRED"
        ) {
          alertErrorMessage(res?.message || "Account verification failed. Try again.");
          return false;
        }
        if (code === "VERIFICATION_METHOD_UNAVAILABLE") {
          const am = res?.available_methods;
          if (Array.isArray(am) && am.length > 0) {
            const rec =
              typeof res?.recommended === "string" && am.includes(res.recommended) ? res.recommended : am[0];
            setAddrBookVerificationOptionsState({
              available_methods: am,
              recommended: rec || String(am[0]),
            });
            addressBookVerificationEntryOpenedRef.current = false;
          }
          alertErrorMessage(res?.message || "This verification method is not available.");
          return false;
        }
        alertErrorMessage(res?.message || "Could not add this withdrawal address.");
        return false;
      }
      const d = res?.data ?? {};
      const wlId = d.id != null ? String(d.id) : "";
      const statusUpper = String(d.status ?? "")
        .toUpperCase()
        .replace(/-/g, "_");

      if (statusUpper === "APPROVED") {
        alertSuccessMessage(res?.message || "Address added and verified successfully.");
        resetSaveAddressFormFields();
        setIsSaveAddressOpen(false);
        setIsRecipientAmountOpen(false);
        await refetchAddressBook();
        return true;
      }

      if (!wlId) {
        alertErrorMessage("Unexpected response from server.");
        return false;
      }

      const method = String(d.method || "").toUpperCase();
      if (method === "METAMASK") {
        const challenge = d.challenge;
        if (!challenge) {
          alertErrorMessage("Server did not return a signing challenge.");
          return false;
        }
        closeAddressBookVerificationUi();
        setAddressWhitelistFlow({
          type: "metamask",
          id: wlId,
          challenge,
          expires_at: d.expires_at || "",
        });
        return true;
      }

      closeAddressBookVerificationUi();
      setAddressWhitelistFlow({
        type: "satoshi",
        id: wlId,
        proof_amount: d.proof_amount != null ? String(d.proof_amount) : "",
        proof_asset: d.proof_asset != null ? String(d.proof_asset) : core.short_name,
        proof_chain: d.proof_chain != null ? String(d.proof_chain) : core.chainCanonical,
        expires_at: d.expires_at || "",
        tokenAssetId: core.tokenAssetIdForApi != null ? String(core.tokenAssetIdForApi).trim() : "",
        shortName: core.short_name,
      });
      return true;
    },
    [refetchAddressBook, resetSaveAddressFormFields, closeAddressBookVerificationUi]
  );


  const flushAddressBookInitiateWithPayload = useCallback(
    async (verificationPayload) => {
      const pending = addressBookPendingInitiateBodyRef.current;
      const core = addressBookPendingInitiateCoreRef.current;
      if (!pending || !core) {
        alertErrorMessage("Session expired. Go back and try again.");
        return false;
      }
      setSaveAddrSaving(true);
      try {
        const res = await initiateAddressBookWhitelist({
          ...pending,
          ...verificationPayload,
        });
        return (await processAddressBookInitiateResponse(res, core)) !== false;
      } catch (e) {
        alertErrorMessage(e?.message || "Could not add this withdrawal address.");
        return false;
      } finally {
        setSaveAddrSaving(false);
      }
    },
    [processAddressBookInitiateResponse]
  );

  const submitAddressBookVerificationFromMethod = useCallback(
    async (method, codeRaw) => {
      const m = String(method || "").trim();
      const code = String(codeRaw || "").trim();
      const methodsAvail = addrBookVerificationOptionsState?.available_methods ?? [];
      if (!m || !methodsAvail.includes(m)) {
        alertErrorMessage("Verification method is no longer valid.");
        return;
      }
      if ((m === "google_authenticator" || m === "email" || m === "mobile") && !code) {
        alertErrorMessage("Enter the verification code.");
        return;
      }
      const ok = await flushAddressBookInitiateWithPayload({
        verification_method: m,
        verification_code: code,
      });
      if (ok) {
        setAddrBookVerifyStage("idle");
        setAddrBookOtpKind(null);
        setAddrBookOtpBoxes(["", "", "", "", "", ""]);
      }
    },
    [addrBookVerificationOptionsState, flushAddressBookInitiateWithPayload]
  );

  const handleAddressBookPasskeyFlow = useCallback(async () => {
    const pending = addressBookPendingInitiateBodyRef.current;
    const core = addressBookPendingInitiateCoreRef.current;
    if (!pending || !core) {
      alertErrorMessage("Session expired. Go back and try again.");
      setAddrBookVerifyStage("idle");
      return;
    }
    try {
      const raw = await fetchAddressBookPasskeyChallenge();
      const opts = raw?.data;
      if (!raw?.success || opts == null || typeof opts !== "object") {
        setAddrBookVerifyStage("idle");
        const pCode = String(raw?.code || "")
          .toUpperCase()
          .replace(/-/g, "_");
        if (pCode !== "PASSKEY_CHALLENGE_EXPIRED" && pCode !== "PASSKEY_VERIFICATION_FAILED") {
          alertErrorMessage(raw?.message || "Could not start passkey verification.");
        }
        return;
      }
      let credential;
      try {
        credential = await startAuthentication({ optionsJSON: opts });
      } catch (webauthnError) {
        setAddrBookVerifyStage("idle");
        const abortName = String(webauthnError?.name || "");
        const userCancelled =
          abortName === "NotAllowedError" ||
          abortName === "AbortError" ||
          webauthnError?.code === 20;
        if (!userCancelled) {
          alertErrorMessage(webauthnError?.message || "Passkey verification failed.");
        }
        return;
      }
      const ok = await flushAddressBookInitiateWithPayload({
        verification_method: "passkey",
        passkey_response: credential,
      });
      if (!ok) {
        setAddrBookVerifyStage("idle");
      } else {
        setAddrBookVerifyStage("idle");
      }
    } catch (e) {
      setAddrBookVerifyStage("idle");
      alertErrorMessage(e?.message || "Passkey verification failed.");
    }
  }, [flushAddressBookInitiateWithPayload]);

  const openAddressBookVerificationEntry = useCallback((st) => {
    const methods = Array.isArray(st?.available_methods) ? st.available_methods : [];
    if (!methods.length) return;

    setEmailVerifyCode("");
    setMobileVerifyCode("");
    setAuthAppCode("");
    setWithdrawVerifyFieldErrors({ email: "", mobile: "", google_authenticator: "", fund_password: "" });
    setAddrBookOtpSending({ email: false, mobile: false });
    setAddrBookOtpResendSec({ email: 0, mobile: 0 });
    setAddrBookOtpBoxes(["", "", "", "", "", ""]);
    setAddrBookOtpKind(null);
    setAddrBookOtpBusy(false);
    setIsWithdrawVerifyCombinedOpen(false);
    setWithdrawOtpFallbackAfterPasskey(false);
    setVerificationContext("address_book");

    let rec =
      st.recommended != null && String(st.recommended).trim() ? String(st.recommended).trim() : "";
    if (!methods.includes(rec)) rec = methods[0] || "";
    setWithdrawVerifyMethod(rec);
    setAddrBookWizardSelectedMethod(rec || null);

    setAddrBookVerifyStage("idle");
  }, []);

  const selectAddressBookVerifyMethod = useCallback(
    async (method) => {
      const m = String(method || "").trim();
      const methodsAvail = addrBookVerificationOptionsState?.available_methods ?? [];
      if (!methodsAvail.includes(m)) return;

      if (m === "passkey") {
        setAddrBookVerifyStage("passkey");
        return;
      }

      setAddrBookOtpKind(m);
      setAddrBookOtpBoxes(["", "", "", "", "", ""]);
      setAddrBookVerifyStage("otp");

      if (m === "email" || m === "mobile") {
        await sendAddressBookSingleOtp(m);
      }
    },
    [addrBookVerificationOptionsState, sendAddressBookSingleOtp]
  );

  const addrBookOtpSetDigit = useCallback((idx, e) => {
    const v = String(e?.target?.value ?? "").replace(/\D/g, "").slice(0, 1);
    setAddrBookOtpBoxes((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
    if (v && idx < 5) {
      requestAnimationFrame(() => document.getElementById(`addr-book-otp-${idx + 1}`)?.focus());
    }
  }, []);

  const addrBookOtpKeyDown = useCallback((idx, e) => {
    if (e.key === "Backspace" && !addrBookOtpBoxes[idx] && idx > 0) {
      document.getElementById(`addr-book-otp-${idx - 1}`)?.focus();
    }
  }, [addrBookOtpBoxes]);

  const addrBookOtpPasteFromClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      const digits = String(text || "").replace(/\D/g, "").slice(0, 6).split("");
      if (digits.length < 6) return;
      setAddrBookOtpBoxes([digits[0], digits[1], digits[2], digits[3], digits[4], digits[5]]);
      document.getElementById("addr-book-otp-5")?.focus();
    } catch {
      // silent
    }
  }, []);

  const submitAddrBookOtpFromBoxes = useCallback(async () => {
    const code = addrBookOtpBoxes.join("");
    if (code.length !== 6) {
      alertErrorMessage("Enter the 6-digit code.");
      return;
    }
    if (!addrBookOtpKind) return;
    setAddrBookOtpBusy(true);
    try {
      await submitAddressBookVerificationFromMethod(addrBookOtpKind, code);
    } finally {
      setAddrBookOtpBusy(false);
    }
  }, [addrBookOtpBoxes, addrBookOtpKind, submitAddressBookVerificationFromMethod]);

  const addrBookBackToMethodPicker = useCallback(() => {
    setAddrBookOtpKind(null);
    setAddrBookOtpBoxes(["", "", "", "", "", ""]);
    setAddrBookVerifyStage("idle");
  }, []);

  const confirmAddrBookWizardVerification = useCallback(() => {
    const m = addrBookWizardSelectedMethod;
    if (!m) {
      alertErrorMessage("Please select a verification method.");
      return;
    }
    void selectAddressBookVerifyMethod(m);
  }, [addrBookWizardSelectedMethod, selectAddressBookVerifyMethod]);

  const resumeAddressBookSecurityVerification = confirmAddrBookWizardVerification;

  useEffect(() => {
    if (!isSaveAddressOpen || saveAddrWizardStep !== SAVE_ADDR_WIZARD_STEP.VERIFY_ACCOUNT) {
      return undefined;
    }
    if (addrBookVerificationOptionsLoading) return undefined;
    const st = addrBookVerificationOptionsState;
    if (!st?.available_methods?.length) return undefined;
    if (addressBookVerificationEntryOpenedRef.current) return undefined;
    addressBookVerificationEntryOpenedRef.current = true;
    openAddressBookVerificationEntry(st);
    return undefined;
  }, [
    isSaveAddressOpen,
    saveAddrWizardStep,
    addrBookVerificationOptionsLoading,
    addrBookVerificationOptionsState,
    openAddressBookVerificationEntry,
  ]);

  const saveAddrWizardBack = useCallback(() => {
    setSaveAddrSubmitAttempted(false);
    setSaveCoinMenuOpen(false);
    setSaveNetworkMenuOpen(false);
    setSaveExchangeMenuOpen(false);

    switch (saveAddrWizardStep) {
      case SAVE_ADDR_WIZARD_STEP.OWNER:
        setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.BASICS);
        setSaveAddrOwnership(SAVE_ADDR_DEFAULT_OWNERSHIP);
        return;
      case SAVE_ADDR_WIZARD_STEP.SELF_WALLET_TYPE:
        setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.OWNER);
        setSaveAddrWalletKindSelf(SAVE_ADDR_DEFAULT_SELF_WALLET_KIND);
        return;
      case SAVE_ADDR_WIZARD_STEP.SELF_EXCHANGE:
        setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.SELF_WALLET_TYPE);
        clearSaveAddrExchangeFields();
        return;
      case SAVE_ADDR_WIZARD_STEP.SELF_VERIFY:
        setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.SELF_WALLET_TYPE);
        return;
      case SAVE_ADDR_WIZARD_STEP.OTHER_IDENTITY:
        setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.OWNER);
        setSaveAddrOwnership(SAVE_ADDR_DEFAULT_OWNERSHIP);
        clearSaveAddrBeneficiaryFields();
        return;
      case SAVE_ADDR_WIZARD_STEP.OTHER_WALLET_TYPE:
        setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.OTHER_IDENTITY);
        setSaveAddrWalletKindOther("");
        return;
      case SAVE_ADDR_WIZARD_STEP.OTHER_EXCHANGE:
        setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.OTHER_WALLET_TYPE);
        clearSaveAddrExchangeFields();
        return;
      case SAVE_ADDR_WIZARD_STEP.VERIFY_ACCOUNT: {
        const back =
          addressBookVerifyReturnStepRef.current || SAVE_ADDR_WIZARD_STEP.SELF_VERIFY;
        addressBookVerificationEntryOpenedRef.current = false;
        closeAddressBookVerificationUi();
        addressBookPendingInitiateBodyRef.current = null;
        addressBookPendingInitiateCoreRef.current = null;
        addressBookVerifyReturnStepRef.current = SAVE_ADDR_WIZARD_STEP.BASICS;
        setAddrBookVerificationOptionsState(null);
        setSaveAddrWizardStep(back);
        return;
      }
      default:
    }
  }, [
    saveAddrWizardStep,
    clearSaveAddrExchangeFields,
    clearSaveAddrBeneficiaryFields,
    closeAddressBookVerificationUi,
  ]);

  const saveAddrWizardGoNext = useCallback(() => {
    setSaveAddrSubmitAttempted(true);
    switch (saveAddrWizardStep) {
      case SAVE_ADDR_WIZARD_STEP.BASICS: {
        if (Object.keys(saveAddrErrors).length > 0) return;
        if (saveAddrBasicsRemoteBlock) return;
        setSaveAddrSubmitAttempted(false);
        setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.OWNER);
        return;
      }
      case SAVE_ADDR_WIZARD_STEP.OWNER:
        if (saveAddrOwnershipError) return;
        setSaveAddrSubmitAttempted(false);
        if (saveAddrOwnership === "SELF") setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.SELF_WALLET_TYPE);
        else if (saveAddrOwnership === "OTHER") setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.OTHER_IDENTITY);
        return;
      case SAVE_ADDR_WIZARD_STEP.SELF_WALLET_TYPE:
        if (saveAddrWalletKindSelfError) return;
        setSaveAddrSubmitAttempted(false);
        if (saveAddrWalletKindSelf === "EXCHANGE") {
          clearSaveAddrExchangeFields();
          setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.SELF_EXCHANGE);
        } else if (saveAddrWalletKindSelf === "SELF_HOSTED") {
          setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.SELF_VERIFY);
        }
        return;
      case SAVE_ADDR_WIZARD_STEP.OTHER_IDENTITY:
        if (Object.keys(saveAddrOtherIdentityErrors).length > 0) return;
        setSaveAddrSubmitAttempted(false);
        setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.OTHER_WALLET_TYPE);
        return;
      case SAVE_ADDR_WIZARD_STEP.OTHER_WALLET_TYPE:
        if (saveAddrWalletKindOtherError) return;
        setSaveAddrSubmitAttempted(false);
        if (saveAddrWalletKindOther === "EXCHANGE") {
          clearSaveAddrExchangeFields();
          setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.OTHER_EXCHANGE);
        } else if (saveAddrWalletKindOther === "SELF_HOSTED") {
          const coreNext = buildAddressBookWizardCoreFields();
          if (!coreNext.address) {
            alertErrorMessage("Address is required.");
            return;
          }
          const bodyOtherSelfHosted = {
            ...buildAddressBookBodyBase(coreNext),
            ownership: "OTHER",
            wallet_type: "NON_CUSTODIAL",
            other_person: otherPersonPayloadFromWizard(),
          };
          beginAddressBookVerificationGate(
            bodyOtherSelfHosted,
            coreNext,
            SAVE_ADDR_WIZARD_STEP.OTHER_WALLET_TYPE
          );
        }
        return;
      default:
    }
  }, [
    saveAddrWizardStep,
    saveAddrErrors,
    saveAddrBasicsRemoteBlock,
    saveAddrOwnership,
    saveAddrOwnershipError,
    saveAddrWalletKindSelf,
    saveAddrWalletKindSelfError,
    saveAddrWalletKindOther,
    saveAddrWalletKindOtherError,
    saveAddrOtherIdentityErrors,
    clearSaveAddrExchangeFields,
    buildAddressBookWizardCoreFields,
    otherPersonPayloadFromWizard,
    beginAddressBookVerificationGate,
  ]);

  const handleSaveAddrExchangeStubSubmit = useCallback(async () => {
    setSaveAddrSubmitAttempted(true);
    if (Object.keys(saveAddrExchangeStepErrors).length > 0) return;

    const ownership =
      saveAddrWizardStep === SAVE_ADDR_WIZARD_STEP.OTHER_EXCHANGE ? "OTHER" : "SELF";
    const core = buildAddressBookWizardCoreFields();
    const exchange_name = resolveAddressBookExchangeName(
      saveAddrExchangePick,
      saveAddrExchangeManual,
      ADDRESS_BOOK_TOP_EXCHANGES,
      ADDRESS_BOOK_EXCHANGE_OTHER
    );
    if (!exchange_name) {
      alertErrorMessage("Please select or enter an exchange name.");
      return;
    }

    const body = {
      ...buildAddressBookBodyBase(core),
      ownership,
      wallet_type: "CUSTODIAL",
      exchange_name,
    };
    if (ownership === "OTHER") {
      body.other_person = otherPersonPayloadFromWizard();
    }

    beginAddressBookVerificationGate(
      body,
      core,
      saveAddrWizardStep === SAVE_ADDR_WIZARD_STEP.OTHER_EXCHANGE
        ? SAVE_ADDR_WIZARD_STEP.OTHER_EXCHANGE
        : SAVE_ADDR_WIZARD_STEP.SELF_EXCHANGE
    );
  }, [
    saveAddrWizardStep,
    saveAddrExchangeStepErrors,
    saveAddrExchangePick,
    saveAddrExchangeManual,
    buildAddressBookWizardCoreFields,
    otherPersonPayloadFromWizard,
    beginAddressBookVerificationGate,
  ]);

  const openFreshAddressBookWizard = useCallback(() => {
    resetSaveAddressFormFields();
    setIsSaveAddressOpen(true);
  }, [resetSaveAddressFormFields]);

  const openAddressBookWizardPrefilledFromWithdraw = useCallback(() => {
    setIsAddressVerificationReminderOpen(false);
    resetSaveAddressFormFields();
    const coin = String(selectedCoin || "").trim();
    const net = selectedNetwork;
    const addr = String(withdrawAddress || "").trim();
    if (coin) setSaveAddrCoin(coin);
    if (net && net !== "Select Network") setSaveAddrNetwork(net);
    if (addr) setSaveAddrValue(addr);
    setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.BASICS);
    setIsSaveAddressOpen(true);
  }, [resetSaveAddressFormFields, selectedCoin, selectedNetwork, withdrawAddress]);

  const handleWhitelistFlowBack = useCallback(() => {
    clearSatoshiWhitelistPoll();
    setAddressWhitelistFlow(null);
    setSatoshiDepositAddress("");
    setSatoshiDepositMemo("");
    setSatoshiDepositLoading(false);
    setSatoshiDepositError(null);
    setWhitelistMetamaskBusy(false);
    setMetamaskInstallModalOpen(false);
    setSaveAddrWizardStep(SAVE_ADDR_WIZARD_STEP.SELF_VERIFY);
  }, [clearSatoshiWhitelistPoll]);

  useEffect(() => {
    if (!addressWhitelistFlow || addressWhitelistFlow.type !== "satoshi") {
      setSatoshiDepositAddress("");
      setSatoshiDepositMemo("");
      setSatoshiDepositLoading(false);
      setSatoshiDepositError(null);
      return undefined;
    }
    const assetId = String(addressWhitelistFlow.tokenAssetId || "").trim();
    const shortName = String(addressWhitelistFlow.shortName || "").trim();
    if (!assetId) {
      setSatoshiDepositAddress("");
      setSatoshiDepositMemo("");
      setSatoshiDepositLoading(false);
      setSatoshiDepositError(
        "Could not resolve deposit details for this network. Use the Deposit page to confirm this asset, or pick another network."
      );
      return undefined;
    }
    let cancelled = false;
    setSatoshiDepositLoading(true);
    setSatoshiDepositError(null);
    setSatoshiDepositAddress("");
    setSatoshiDepositMemo("");
    (async () => {
      try {
        const res = await AuthService.getAndGenerateAddress(assetId, shortName, true);
        if (cancelled) return;
        if (res?.success === false) {
          setSatoshiDepositError(res?.message || "Could not load deposit address.");
          return;
        }
        const parsed = parseWalletDepositAddressResponse(res);
        if (!parsed.address || !String(parsed.address).trim()) {
          setSatoshiDepositError("No deposit address returned.");
          return;
        }
        setSatoshiDepositAddress(String(parsed.address).trim());
        setSatoshiDepositMemo(parsed.memo != null ? String(parsed.memo) : "");
        setSatoshiDepositError(null);
      } catch {
        if (!cancelled) setSatoshiDepositError("Could not load deposit address.");
      } finally {
        if (!cancelled) setSatoshiDepositLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addressWhitelistFlow]);

  const copySatoshiDepositAddress = useCallback(() => {
    const t = String(satoshiDepositAddress || "").trim();
    if (!t) return;
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(t).then(() => alertSuccessMessage("Address copied"));
    }
  }, [satoshiDepositAddress]);

  const copySatoshiDepositMemo = useCallback(() => {
    const t = String(satoshiDepositMemo || "").trim();
    if (!t) return;
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(t).then(() => alertSuccessMessage("Memo copied"));
    }
  }, [satoshiDepositMemo]);

  const handleSatoshiWhitelistSent = useCallback(async () => {
    if (!addressWhitelistFlow || addressWhitelistFlow.type !== "satoshi") return;
    if (satoshiWhitelistConfirmInFlightRef.current) return;
    const id = addressWhitelistFlow.id;
    if (!id) return;

    satoshiWhitelistConfirmInFlightRef.current = true;
    setSaveAddrSatoshiPolling(true);
    try {
      const res = await confirmSatoshiAddressBook(id);
      if (res?.success === false) {
        const code = String(res?.code || "")
          .toUpperCase()
          .replace(/-/g, "_");
        if (code === "PROOF_EXPIRED") {
          clearSatoshiWhitelistPoll();
          alertErrorMessage(res?.message || "Proof window expired. Please start over.");
          setAddressWhitelistFlow(null);
          return;
        }
        if (satoshiConfirmIndicatesProofStillPending(res)) {
          setSatoshiWhitelistAwaitingProof(true);
          return;
        }
        clearSatoshiWhitelistPoll();
        alertErrorMessage(res?.message || "Could not confirm deposit.");
        return;
      }

      const st = String(res?.data?.status || "")
        .toUpperCase()
        .replace(/-/g, "_");
      if (st === "APPROVED") {
        clearSatoshiWhitelistPoll();
        alertSuccessMessage(res?.message || "Address whitelisted successfully.");
        resetSaveAddressFormFields();
        setIsSaveAddressOpen(false);
        setIsRecipientAmountOpen(false);
        await refetchAddressBook();
        return;
      }
      if (st === "REJECTED") {
        clearSatoshiWhitelistPoll();
        alertErrorMessage(res?.message || "Address could not be whitelisted.");
        setAddressWhitelistFlow(null);
        return;
      }
      if (st === "EXPIRED" || st === "PROOF_EXPIRED") {
        clearSatoshiWhitelistPoll();
        alertErrorMessage(res?.message || "Proof window expired. Please start over.");
        setAddressWhitelistFlow(null);
        return;
      }
      if (satoshiConfirmIndicatesProofStillPending(res)) {
        setSatoshiWhitelistAwaitingProof(true);
        return;
      }
      setSatoshiWhitelistAwaitingProof(true);
    } catch {
      clearSatoshiWhitelistPoll();
      alertErrorMessage("Could not confirm deposit.");
    } finally {
      satoshiWhitelistConfirmInFlightRef.current = false;
      setSaveAddrSatoshiPolling(false);
    }
  }, [addressWhitelistFlow, clearSatoshiWhitelistPoll, refetchAddressBook, resetSaveAddressFormFields]);

  const handleMetaMaskWhitelistSign = useCallback(async () => {
    if (!addressWhitelistFlow || addressWhitelistFlow.type !== "metamask") return;

    const mmReady = hasMetaMaskExtension();
    setEip1193BrowserProviderDetected(mmReady);

    if (!mmReady) {
      setMetamaskInstallModalOpen(true);
      return;
    }

    const { id, challenge } = addressWhitelistFlow;
    setWhitelistMetamaskBusy(true);
    try {
      const signature = await promiseWithTimeout(
        signChallengeWithMetaMask(challenge),
        METAMASK_SIGN_FLOW_TIMEOUT_MS,
        "Your wallet did not respond. Install or enable MetaMask (or another Ethereum wallet), unlock it, allow this site, then try again."
      );
      const res = await verifySignatureAddressBook(id, signature);
      if (res?.success === false) {
        const code = String(res?.code || "")
          .toUpperCase()
          .replace(/-/g, "_");
        if (code === "AML_BLOCKED") {
          alertErrorMessage(
            res?.message || "This address could not be whitelisted for compliance reasons."
          );
        } else if (code === "CHALLENGE_EXPIRED") {
          alertErrorMessage(res?.message || "Verification window expired. Please start over.");
          setAddressWhitelistFlow(null);
        } else if (code === "SIGNATURE_INVALID" || code === "SIGNATURE_MISMATCH") {
          alertErrorMessage(
            res?.message ||
              "Signature could not be verified. Use the correct MetaMask account and try again."
          );
        } else {
          alertErrorMessage(res?.message || "Verification failed");
        }
        return;
      }
      alertSuccessMessage(res?.message || "Address whitelisted successfully.");
      resetSaveAddressFormFields();
      setIsSaveAddressOpen(false);
      setIsRecipientAmountOpen(false);
      await refetchAddressBook();
    } catch (e) {
      const msg = e?.message || "Could not sign with MetaMask";
      alertErrorMessage(msg);
      if (/did not respond|not available/i.test(String(msg))) {
        setMetamaskInstallModalOpen(true);
      }
    } finally {
      setWhitelistMetamaskBusy(false);
    }
  }, [addressWhitelistFlow, refetchAddressBook, resetSaveAddressFormFields]);

  const resumeMetaMaskWhitelistFromAddressBook = useCallback((entry) => {
    if (!addressBookEntryPendingMetaMaskSignature(entry)) return;
    const eid = entry._id ?? entry.id;
    const challenge = String(entry.metamask_challenge ?? entry.metamaskChallenge ?? "").trim();
    const expiresAt = addressBookMetaMaskChallengeExpiryIso(entry);
    if (expiresAt) {
      const t = Date.parse(expiresAt);
      if (Number.isFinite(t) && Date.now() > t) {
        alertErrorMessage(
          "This MetaMask verification window has expired. Remove the address and add it again to get a new challenge."
        );
        return;
      }
    }
    setEip1193BrowserProviderDetected(hasMetaMaskExtension());
    setAddressWhitelistFlow({
      type: "metamask",
      id: String(eid),
      challenge,
      expires_at: expiresAt,
    });
    setShowAddressBookPanel(false);
    setIsSaveAddressOpen(true);
  }, []);

  const resumeSatoshiWhitelistFromAddressBook = useCallback(
    (entry) => {
      if (!addressBookEntryPendingSatoshiDeposit(entry)) return;
      const expiresRaw = entry.satoshi_expires_at ?? entry.satoshiExpiresAt ?? null;
      const expiresAt = expiresRaw != null && String(expiresRaw).trim() ? String(expiresRaw).trim() : "";
      if (expiresAt) {
        const t = Date.parse(expiresAt);
        if (Number.isFinite(t) && Date.now() > t) {
          alertErrorMessage(
            "This deposit verification window has expired. Remove the address and add it again."
          );
          return;
        }
      }
      const tokenAssetId = resolveAddressBookTokenAssetId(entry, withdrawCoins);
      if (!tokenAssetId) {
        alertErrorMessage(
          "Could not load deposit instructions for this entry. Remove it and add the address again, or check that this coin and network exist in the withdrawal list."
        );
        return;
      }
      const eid = entry._id ?? entry.id;
      const proof_amount = String(
        entry.satoshi_amount ?? entry.proof_amount ?? entry.satoshiAmount ?? entry.proofAmount ?? ""
      ).trim();
      const proof_asset = String(
        entry.satoshi_asset ?? entry.proof_asset ?? entry.coin ?? entry.short_name ?? ""
      )
        .trim()
        .toUpperCase();
      const proof_chain = String(entry.satoshi_chain ?? entry.proof_chain ?? entry.chain ?? "")
        .trim()
        .toUpperCase();
      const shortName = String(entry.coin ?? entry.short_name ?? proof_asset ?? "").trim().toUpperCase();

      clearSatoshiWhitelistPoll();
      setAddressWhitelistFlow({
        type: "satoshi",
        id: String(eid),
        proof_amount,
        proof_asset: proof_asset || shortName,
        proof_chain: proof_chain || String(entry.chain || "").toUpperCase(),
        expires_at: expiresAt,
        tokenAssetId,
        shortName: shortName || proof_asset,
      });
      setShowAddressBookPanel(false);
      setIsSaveAddressOpen(true);
    },
    [withdrawCoins, clearSatoshiWhitelistPoll]
  );

  const handleSaveAddressBookSubmit = useCallback(async () => {
    setSaveAddrSubmitAttempted(true);
    if (saveAddrWizardStep !== SAVE_ADDR_WIZARD_STEP.SELF_VERIFY) return;
    if (!isSaveAddrSelfVerifyValid) return;

    const core = buildAddressBookWizardCoreFields();
    const chainCanonical = core.chainCanonical;
    const method = isMetaMaskWhitelistChain(chainCanonical) ? saveAddrWhitelistMethod : "SATOSHI";

    const body = {
      ...buildAddressBookBodyBase(core),
      ownership: "SELF",
      wallet_type: "NON_CUSTODIAL",
      method,
    };
    beginAddressBookVerificationGate(body, core, SAVE_ADDR_WIZARD_STEP.SELF_VERIFY);
  }, [
    saveAddrWizardStep,
    isSaveAddrSelfVerifyValid,
    buildAddressBookWizardCoreFields,
    saveAddrWhitelistMethod,
    beginAddressBookVerificationGate,
  ]);

  const openAddressBookDeleteConfirm = useCallback((entry) => {
    const eid = entry._id || entry.id;
    if (eid == null || eid === "") return;
    const addr = entry.address || "";
    const shortAddr = addr ? truncateMid(addr, 8, 6) : "—";
    const statusUpper = entry.status != null ? String(entry.status).toUpperCase().trim() : "";
    setAddressBookDeleteConfirm({
      entryId: eid,
      label: String(entry.label || "Saved").trim() || "Saved",
      shortAddr,
      statusUpper,
      chain: entry.chain != null ? String(entry.chain).toUpperCase() : "",
      coin: entry.coin != null ? String(entry.coin).toUpperCase() : "",
    });
  }, []);

  const confirmAddressBookDelete = useCallback(async () => {
    const entryId = addressBookDeleteConfirm?.entryId;
    if (entryId == null || entryId === "") return;
    setAddressBookDeleteBusy(true);
    try {
      const res = await deleteWalletAddressBook(entryId);
      if (res?.success !== false) {
        alertSuccessMessage(res?.message || "Address removed");
        setAddressBookDeleteConfirm(null);
        await refetchAddressBook();
      } else {
        alertErrorMessage(res?.message || "Could not remove address");
      }
    } catch {
      alertErrorMessage("Could not remove address");
    } finally {
      setAddressBookDeleteBusy(false);
    }
  }, [addressBookDeleteConfirm, refetchAddressBook]);

  const withdrawProfileReady = useMemo(() => Object.keys(userDetails || {}).length > 0, [userDetails]);
  /** True until the first `handleUserDetails` completes after login (avoids a permanent "loading" overlay when `userDetails` is still `{}` on hard refresh). */
  const withdrawProfileLoading = useMemo(() => !profileHydrated, [profileHydrated]);
  const withdrawKycVerified = useMemo(() => Number(userDetails?.kycVerified) === 2, [userDetails?.kycVerified]);
  const showWithdrawKycGateOverlay = useMemo(
    () => withdrawProfileLoading || (withdrawProfileReady && !withdrawKycVerified),
    [withdrawProfileLoading, withdrawProfileReady, withdrawKycVerified]
  );

  const addrBookMaskedEmailDisplay = useMemo(
    () => maskAddrBookEmail(userDetails?.emailId || ""),
    [userDetails?.emailId]
  );
  const addrBookMaskedPhoneDisplay = useMemo(
    () => maskAddrBookPhone(userDetails?.mobileNumber || ""),
    [userDetails?.mobileNumber]
  );

  return {
    navigate,
    isMobile,
    setIsMobile,
    stepperWrapRef,
    railDims,
    setRailDims,
    withdrawSubmitting,
    setWithdrawSubmitting,
    emailVerifyCode,
    setEmailVerifyCode,
    withdrawOtpSending,
    setWithdrawOtpSending,
    withdrawOtpResendSec,
    setWithdrawOtpResendSec,
    withdrawWithdrawalFlowBusy,
    setWithdrawWithdrawalFlowBusy,
    withdrawConfirmBusy,
    setWithdrawConfirmBusy,
    withdrawSummaryContinueBusy,
    setWithdrawSummaryContinueBusy,
    withdrawalRequestLockRef,
    withdrawHistoryPromiseRef,
    selectedCoin,
    setSelectedCoin,
    withdrawCoins,
    setWithdrawCoins,
    loadingWithdrawCoins,
    setLoadingWithdrawCoins,
    coinSearchQuery,
    setCoinSearchQuery,
    recentWithdrawRows,
    setRecentWithdrawRows,
    loadingWithdrawHistory,
    setLoadingWithdrawHistory,
    fundMain,
    setFundMain,
    selectedWithdrawHistoryRow,
    setSelectedWithdrawHistoryRow,
    selectedNetwork,
    setSelectedNetwork,
    isWithdrawDetailsOpen,
    withdrawToTab,
    setWithdrawToTab,
    agceRecipientTab,
    setAgceRecipientTab,
    agceRecipientEmail,
    setAgceRecipientEmail,
    agceRecipientPhone,
    setAgceRecipientPhone,
    agceRecipientId,
    setAgceRecipientId,
    agceRecipientUsername,
    agceTouched,
    setAgceTouched,
    showAddressBookPanel,
    setShowAddressBookPanel,
    addressBookSubTab,
    setAddressBookSubTab,
    withdrawAddress,
    setWithdrawAddress,
    withdrawAddressTouched,
    setWithdrawAddressTouched,
    withdrawAddressValidError,
    setWithdrawAddressValidError,
    withdrawAddressValidatedKey,
    setWithdrawAddressValidatedKey,
    isWithdrawAddressValidating,
    setIsWithdrawAddressValidating,
    withdrawAddressValidationReqIdRef,
    withdrawAddrValidateDebounceTimerRef,
    withdrawConfirmProceedTimerRef,
    satoshiWhitelistConfirmInFlightRef,
    isAvailSourceOpen,
    setIsAvailSourceOpen,
    isAvailSourcePinned,
    setIsAvailSourcePinned,
    isAvailSourceContextLock,
    setIsAvailSourceContextLock,
    isNetworkFeeInfoOpen,
    setIsNetworkFeeInfoOpen,
    withdrawAmountInput,
    setWithdrawAmountInput,
    withdrawAmountTouched,
    setWithdrawAmountTouched,
    isLimitInfoOpen,
    setIsLimitInfoOpen,
    isWithdrawMetaRefreshing,
    setIsWithdrawMetaRefreshing,
    availSourceRef,
    phoneCountry,
    setPhoneCountry,
    isCountryOpen,
    setIsCountryOpen,
    countryRef,
    isWithdrawConfirmOpen,
    setIsWithdrawConfirmOpen,
    withdrawConfirmRef,
    isAddressVerificationReminderOpen,
    setIsAddressVerificationReminderOpen,
    isWithdrawSummaryOpen,
    setIsWithdrawSummaryOpen,
    withdrawSummaryRef,
    isPasskeyOpen,
    setIsPasskeyOpen,
    passkeyRef,
    isAuthAppOpen,
    setIsAuthAppOpen,
    authAppRef,
    authAppCode,
    setAuthAppCode,
    isChooseAuthOpen,
    setIsChooseAuthOpen,
    chooseAuthRef,
    isEmailVerifyOpen,
    setIsEmailVerifyOpen,
    emailVerifyRef,
    isMobileVerifyOpen,
    setIsMobileVerifyOpen,
    mobileVerifyRef,
    isSecUnavailableOpen,
    setIsSecUnavailableOpen,
    secUnavailable,
    setSecUnavailable,
    isSecResetConfirmOpen,
    setIsSecResetConfirmOpen,
    secResetAck,
    setSecResetAck,
    isSecReqOpen,
    setIsSecReqOpen,
    secReqDone,
    setSecReqDone,
    isRequestSubmittedOpen,
    setIsRequestSubmittedOpen,
    isSecurityResetSuccessOpen,
    setIsSecurityResetSuccessOpen,
    verificationContext,
    setVerificationContext,
    withdrawSecuritySettings,
    setWithdrawSecuritySettings,
    withdrawVerifyMethod,
    setWithdrawVerifyMethod,
    withdrawFundPassword,
    setWithdrawFundPassword,
    withdrawFundPasswordError,
    setWithdrawFundPasswordError,
    withdrawFundPasswordVisible,
    setWithdrawFundPasswordVisible,
    isNoMethodsModalOpen,
    setIsNoMethodsModalOpen,
    isWithdrawVerifyCombinedOpen,
    setIsWithdrawVerifyCombinedOpen,
    withdrawVerifyFieldErrors,
    setWithdrawVerifyFieldErrors,
    mobileVerifyCode,
    setMobileVerifyCode,
    withdrawOtpFallbackAfterPasskey,
    setWithdrawOtpFallbackAfterPasskey,
    closeAllOverlays,
    isAwaitingApprovalOpen,
    setIsAwaitingApprovalOpen,
    awaitingApprovalRef,
    isRecipientAmountOpen,
    setIsRecipientAmountOpen,
    recipientAmountRef,
    isSetPayPinOpen,
    setIsSetPayPinOpen,
    payPinDigits,
    setPayPinDigits,
    payPinShow,
    setPayPinShow,
    payPinRefs,
    isSaveAddressOpen,
    setIsSaveAddressOpen,
    saveAddrLabel,
    setSaveAddrLabel,
    saveAddrCoin,
    setSaveAddrCoin,
    saveAddrUniversal,
    setSaveAddrUniversal,
    saveAddrValue,
    setSaveAddrValue,
    saveAddrNetwork,
    setSaveAddrNetwork,
    saveAddrMemo,
    setSaveAddrMemo,
    saveAddrWhitelistMethod,
    setSaveAddrWhitelistMethod,
    addrBookVerificationOptionsState,
    addrBookVerificationOptionsLoading,
    addrBookVerifyStage,
    addrBookOtpKind,
    addrBookOtpBoxes,
    setAddrBookOtpBoxes,
    addrBookOtpBusy,
    addrBookOtpSending,
    addrBookOtpResendSec,
    addrBookWizardSelectedMethod,
    setAddrBookWizardSelectedMethod,
    addrBookMaskedEmailDisplay,
    addrBookMaskedPhoneDisplay,
    sendAddressBookSingleOtp,
    flushAddressBookInitiateWithPayload,
    submitAddressBookVerificationFromMethod,
    handleAddressBookPasskeyFlow,
    openAddressBookVerificationEntry,
    selectAddressBookVerifyMethod,
    confirmAddrBookWizardVerification,
    addrBookOtpSetDigit,
    addrBookOtpKeyDown,
    addrBookOtpPasteFromClipboard,
    submitAddrBookOtpFromBoxes,
    addrBookBackToMethodPicker,
    resumeAddressBookSecurityVerification,
    closeAddressBookVerificationUi,
    saveAddrWizardStep,
    setSaveAddrWizardStep,
    saveAddrOwnership,
    setSaveAddrOwnership,
    saveAddrWalletKindSelf,
    setSaveAddrWalletKindSelf,
    saveAddrWalletKindOther,
    setSaveAddrWalletKindOther,
    saveAddrExchangePick,
    setSaveAddrExchangePick,
    saveAddrExchangeManual,
    setSaveAddrExchangeManual,
    saveAddrDeclarationAccepted,
    setSaveAddrDeclarationAccepted,
    saveAddrBenFullName,
    setSaveAddrBenFullName,
    saveAddrBenPan,
    setSaveAddrBenPan,
    saveAddrBenCountry,
    setSaveAddrBenCountry,
    saveAddrBenPin,
    setSaveAddrBenPin,
    saveAddrBenAddress,
    setSaveAddrBenAddress,
    addressWhitelistFlow,
    setAddressWhitelistFlow,
    satoshiDepositAddress,
    setSatoshiDepositAddress,
    satoshiDepositMemo,
    setSatoshiDepositMemo,
    satoshiDepositLoading,
    setSatoshiDepositLoading,
    satoshiDepositError,
    setSatoshiDepositError,
    saveAddrSatoshiPolling,
    setSaveAddrSatoshiPolling,
    satoshiWhitelistAwaitingProof,
    setSatoshiWhitelistAwaitingProof,
    whitelistMetamaskBusy,
    setWhitelistMetamaskBusy,
    metamaskInstallModalOpen,
    setMetamaskInstallModalOpen,
    eip1193BrowserProviderDetected,
    setEip1193BrowserProviderDetected,
    saveAddrSubmitAttempted,
    setSaveAddrSubmitAttempted,
    saveAddrSaving,
    setSaveAddrSaving,
    saveAddrBasicsNextDisabled,
    saveAddrAddressValidating,
    saveAddrAddressValidError,
    validateSaveAddrBookAddress,
    saveAddrValidateDebounceTimerRef,
    clearSaveAddrAddressValidationForEdit,
    addressBookEntries,
    setAddressBookEntries,
    addressBookLoading,
    setAddressBookLoading,
    withdrawalAddressHistory,
    setWithdrawalAddressHistory,
    withdrawalAddressHistoryLoading,
    setWithdrawalAddressHistoryLoading,
    addressBookDeleteConfirm,
    setAddressBookDeleteConfirm,
    addressBookDeleteBusy,
    setAddressBookDeleteBusy,
    saveCoinMenuOpen,
    setSaveCoinMenuOpen,
    saveNetworkMenuOpen,
    setSaveNetworkMenuOpen,
    saveExchangeMenuOpen,
    setSaveExchangeMenuOpen,
    saveCoinSelectRef,
    saveNetworkSelectRef,
    saveExchangeSelectRef,
    isWithdrawHistoryDetailOpen,
    setIsWithdrawHistoryDetailOpen,
    hideWithdrawErrorNotices,
    setHideWithdrawErrorNotices,
    recentWithdrawTab,
    setRecentWithdrawTab,
    recentWithdrawRowsVisible,
    withdraw24hUsage,
    setWithdraw24hUsage,
    reloadWithdrawCatalog,
    reloadWithdrawal24hUsage,
    refetchWithdrawHistory,
    refetchAddressBook,
    refetchWithdrawalAddressHistory,
    payPinValue,
    isPayPinComplete,
    resetPayPin,
    handlePayPinDigit,
    handlePayPinKeyDown,
    isStep2Done,
    selectedCoinObj,
    isStep1Done,
    activeWithdrawNetworks,
    minWithdrawForSelection,
    effectiveAvailable,
    filteredWithdrawCoins,
    trendingWithdrawCoins,
    coinModalDisplay,
    coinQuickPickDisplay,
    saveAddrCoinOptionsList,
    saveAddrNetworkOptionsEffective,
    saveAddrChainCanonical,
    selectedCoinMeta,
    isWithdrawFormValid,
    withdrawAddressInlineError,
    withdrawAmountInlineError,
    summaryNetworkLabel,
    selectedTokenAssetId,
    validateCurrentWithdrawAddress,
    validateCurrentWithdrawAddressRef,
    applyWithdrawalAddressHistoryEntry,
    withdrawalAddressIsApprovedInBook,
    withdrawStep3Preview,
    getNonPasskeyEnabledMethods,
    hasConfigurableNonPasskeyMethods,
    withdrawCombinedVerificationReady,
    sendSingleWithdrawalOtp,
    withdrawOtpResendActive,
    submitWithdrawalWithEnabledMethods,
    handlePasskeyWithdrawFlow,
    handleWithdrawalSubmitFromCode,
    handleWithdrawalSubmitFundPassword,
    openWithdrawVerificationModal,
    openWithdrawOtpFallbackFromPasskey,
    openWithdrawVerificationCombinedOnly,
    shouldShowPasskeyLink,
    shouldShowSwitchMethodLink,
    openSecurityUnavailableFlow,
    openPasskeysOnlyRequirements,
    closeResetResultOverlays,
    secUnavailableSelectedCount,
    secRequirementsMode,
    secReqTotal,
    secReqCompleted,
    agceErrors,
    handleAgceEmailChange,
    handleAgcePhoneChange,
    handleWithdrawMaxClick,
    handleWithdrawAmountChange,
    copyWithdrawText,
    pasteWithdrawAuthAppCode,
    onWithdrawAgainFromHistory,
    cancelPendingWithdrawal,
    withdrawCancelBusy,
    saveAddrErrors,
    isSaveAddrValid,
    saveAddrOtherIdentityErrors,
    saveAddrOwnershipError,
    saveAddrWalletKindSelfError,
    saveAddrWalletKindOtherError,
    saveAddrExchangeStepErrors,
    isSaveAddrSelfVerifyValid,
    saveAddrCoinDisplay,
    saveAddrNetworkDisplay,
    clearSatoshiWhitelistPoll,
    resetSaveAddressFormFields,
    handleWhitelistFlowBack,
    copySatoshiDepositAddress,
    copySatoshiDepositMemo,
    handleSatoshiWhitelistSent,
    handleMetaMaskWhitelistSign,
    resumeMetaMaskWhitelistFromAddressBook,
    resumeSatoshiWhitelistFromAddressBook,
    handleSaveAddressBookSubmit,
    saveAddrWizardBack,
    saveAddrWizardGoNext,
    handleSaveAddrExchangeStubSubmit,
    openFreshAddressBookWizard,
    openAddressBookWizardPrefilledFromWithdraw,
    openAddressBookDeleteConfirm,
    confirmAddressBookDelete,
    withdrawProfileLoading,
    withdrawProfileReady,
    withdrawKycVerified,
    showWithdrawKycGateOverlay
  };

}
